import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AssessmentIcon from "@mui/icons-material/Assessment";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { ErrorMessage } from "../../../../commons/Toast/ErrorMessage";
import {
  getConsumersByCompId,
  getGeneratorsByCompId,
} from "../../../../Services/GNA/GetData";
import {
  getAuditPaymentLogs,
  getAuditListingLogs,
  getAuditListingDetail,
  getAuditContract,
} from "../../../../Services/GNA/AuditApi";
import AuditListingDetailView from "./AuditListingDetailView";
import Styles from "./AuditModal.module.css";

// ─── Audit type config ────────────────────────────────────────────────────────
const AUDIT_TYPES = [
  { value: "PAYMENT_ACTIVITY", label: "Payment Activity" },
  { value: "ALL_LISTING", label: "All Listing" },
  { value: "LISTING_NUMBER", label: "Listing No." },
  { value: "CONTRACT_ID", label: "Contract ID" },
];

// Types that use Start/End date range for filtering
const DATE_RANGE_TYPES = new Set(["PAYMENT_ACTIVITY", "ALL_LISTING"]);
// Types that use a single lookup field instead
const LISTING_NO_TYPE = "LISTING_NUMBER";
const CONTRACT_ID_TYPE = "CONTRACT_ID";

// ─── Column definitions ───────────────────────────────────────────────────────
const PAYMENT_COLS = [
  { key: "dateTime", label: "Date & Time" },
  { key: "activity", label: "Activity" },
  { key: "amount", label: "Amount (₹)" },
  { key: "paymentType", label: "Payment Type" },
  { key: "listingId", label: "Listing ID" },
  { key: "counterPartyName", label: "Counter Party" },
  { key: "counterPartyListingId", label: "CP Listing" },
  { key: "bilateralContractUniqueId", label: "Contract ID" },
  { key: "comments", label: "Comments" },
];

const LISTING_COLS = [
  { key: "timestamp", label: "Timestamp" },
  { key: "activityType", label: "Activity Type" },
  { key: "companyName", label: "Company" },
  { key: "entityName", label: "Entity" },
  { key: "listingId", label: "Listing ID" },
  { key: "referenceId", label: "Reference ID" },
  { key: "status", label: "Status" },
  { key: "settlementStatus", label: "Settlement" },
  { key: "user", label: "User" },
  { key: "remarks", label: "Remarks" },
];

const TRANSACTION_COLS = [
  { key: "id", label: "ID" },
  { key: "consumerId", label: "Consumer Listing" },
  { key: "againstGeneratorId", label: "Generator Listing" },
  { key: "viewCharges", label: "Charges" },
  { key: "status", label: "Status" },
  { key: "amount", label: "Amount (₹)" },
];

const getAuditConsumerListing = (row) => {
  const parts = String(row?.name ?? "").split("-");
  return parts[0]?.trim() || row?.consumerId || "—";
};

const getAuditGeneratorListing = (row) => {
  const parts = String(row?.name ?? "").split("-");
  return parts[1]?.trim() || row?.againstGeneratorId || "—";
};

const NOCA_COLS = [
  { key: "docId", label: "Doc ID" },
  { key: "documentType", label: "Document Type" },
  { key: "referenceType", label: "Reference Type" },
  { key: "status", label: "Status" },
];

const NOCA_EXPORT_COLS = [
  { key: "docId", label: "Doc ID" },
  { key: "documentType", label: "Document Type" },
  { key: "referenceType", label: "Reference Type" },
  { key: "nocaStatus", label: "NOCA Status" },
  { key: "transactionId", label: "Transaction ID" },
  { key: "transactionType", label: "Type" },
  { key: "transferAmount", label: "Amount (₹)" },
  { key: "fromUser", label: "From" },
  { key: "toUser", label: "To" },
  { key: "receiverType", label: "Receiver Type" },
  { key: "txnStatus", label: "Txn Status" },
  { key: "txnFor", label: "Transaction For" },
  { key: "txnDateTime", label: "Date & Time" },
];

const BANK_TXN_COLS = [
  { key: "transactionId", label: "Transaction ID" },
  { key: "transactionType", label: "Type" },
  { key: "transferAmount", label: "Amount (₹)" },
  { key: "fromUser", label: "From" },
  { key: "toUser", label: "To" },
  { key: "receiverType", label: "Receiver Type" },
  { key: "status", label: "Status" },
  { key: "txnFor", label: "Transaction For" },
  { key: "txnDateTime", label: "Date & Time" },
];

const CONSUMER_LISTING_COLS = [
  { key: "id", label: "ID" },
  { key: "name", label: "Listing Name" },
  { key: "status", label: "Status" },
  { key: "priceDiscoveryType", label: "Discovery Type" },
  { key: "region", label: "Region" },
  { key: "voltageLevel", label: "Voltage Level" },
  { key: "listingType", label: "Listing Type" },
  { key: "createdAt", label: "Created At" },
];

const CONTRACT_DETAIL_COLS = [
  { key: "contractUniqueId", label: "Contract ID" },
  { key: "consumerName", label: "Consumer" },
  { key: "generatorName", label: "Generator" },
  { key: "capacityConsumerName", label: "Consumer Listing" },
  { key: "capacityGeneratorName", label: "Generator Listing" },
  { key: "status", label: "Status" },
  { key: "schedulingStatus", label: "Scheduling" },
];

const WALLET_EXPORT_COLS = [
  { key: "bilateralContractUniqueId", label: "Contract ID" },
  { key: "listingId", label: "Listing ID" },
  { key: "counterPartyListingId", label: "CP Listing ID" },
  { key: "dateTime", label: "Date & Time" },
  { key: "activity", label: "Activity" },
  { key: "amount", label: "Amount (₹)" },
  { key: "paymentType", label: "Payment Type" },
  { key: "counterPartyName", label: "Counter Party" },
  { key: "comments", label: "Comments" },
];

const CONTRACTS_EXPORT_COLS = [
  { key: "id", label: "ID" },
  { key: "consumerListing", label: "Consumer Listing" },
  { key: "generatorListing", label: "Generator Listing" },
  { key: "status", label: "Status" },
  { key: "type", label: "Type" },
  { key: "platform", label: "Platform" },
  { key: "amount", label: "Amount (₹)" },
  { key: "volume", label: "Volume" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "scheduleDurationDays", label: "Duration (Days)" },
  { key: "blockedAmountDurationDays", label: "Blocked Amt. Duration (Days)" },
  { key: "noOfHours", label: "No. of Hours" },
  { key: "paymentType", label: "Payment Type" },
  { key: "paymentMode", label: "Payment Mode" },
  { key: "transferMode", label: "Transfer Mode" },
  { key: "date", label: "Date" },
  { key: "dateTime", label: "Date & Time" },
  { key: "isScheduled", label: "Scheduled" },
  { key: "immediateTransaction", label: "Immediate" },
  { key: "transactionId", label: "Transaction ID" },
  { key: "againstTransactionId", label: "Against Transaction ID" },
  { key: "totalCharges", label: "Total Charges (₹)" },
  { key: "totalConsumerCharges", label: "Consumer Charges (₹)" },
  { key: "totalGeneratorCharges", label: "Generator Charges (₹)" },
  { key: "walletId", label: "Wallet ID" },
  { key: "walletType", label: "Wallet Account Type" },
  { key: "walletCompanyCode", label: "Wallet Company Code" },
  { key: "walletBalance", label: "Wallet Balance (₹)" },
  { key: "walletBlocked", label: "Wallet Blocked (₹)" },
  { key: "remarks", label: "Remarks" },
];

const CHARGES_EXPORT_COLS = [
  { key: "transactionId", label: "Transaction ID" },
  { key: "transactionName", label: "Transaction Name" },
  { key: "name", label: "Charge Name" },
  { key: "chargeType", label: "Charge Type" },
  { key: "type", label: "Type" },
  { key: "value", label: "Value" },
  { key: "frequency", label: "Frequency" },
  { key: "bearer", label: "Bearer" },
  { key: "payer", label: "Payer" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const INIT_FILTERS = {
  companyId: "",
  entityId: "",
  entityType: "",
  auditType: "PAYMENT_ACTIVITY",
  startDate: "",
  endDate: "",
  listingNumber: "",
  contractId: "",
};

// ─── Export helpers ───────────────────────────────────────────────────────────
const downloadCSV = (rows, cols, name) => {
  const header = cols.map((c) => c.label).join(",");
  const body = rows.map((r) =>
    cols
      .map((c) => `"${String(r[c.key] ?? "").replace(/"/g, '""')}"`)
      .join(","),
  );
  const blob = new Blob([[header, ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name + ".csv";
  a.click();
};

const downloadExcel = async (rows, cols, name) => {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Audit Log");
    ws.columns = cols.map((c) => ({ header: c.label, key: c.key, width: 22 }));
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF1935C" },
    };
    rows.forEach((r) => ws.addRow(r));
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name + ".xlsx";
    a.click();
  } catch {
    ErrorMessage("Excel export failed");
  }
};

const downloadPDF = async (rows, cols, name, title) => {
  try {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ orientation: "landscape" });
    const pageW = doc.internal.pageSize.width;
    const tableW = pageW - 28;
    const colW = tableW / cols.length;

    doc.setFontSize(13);
    doc.text(title, 14, 16);
    doc.setFontSize(8);
    doc.text(
      `Generated: ${new Date().toLocaleString("en-IN")}  |  Total Records: ${rows.length}`,
      14,
      23,
    );

    let y = 32;
    doc.setFillColor(241, 147, 92);
    doc.rect(14, y - 5, tableW, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    cols.forEach((c, i) =>
      doc.text(c.label.slice(0, 13), 14 + i * colW + 1, y),
    );

    doc.setTextColor(40, 40, 40);
    y += 10;

    rows.slice(0, 200).forEach((row, idx) => {
      if (y > doc.internal.pageSize.height - 20) {
        doc.addPage();
        y = 20;
      }
      if (idx % 2 === 0) {
        doc.setFillColor(248, 249, 250);
        doc.rect(14, y - 5, tableW, 7, "F");
      }
      cols.forEach((c, i) =>
        doc.text(String(row[c.key] ?? "").slice(0, 15), 14 + i * colW + 1, y),
      );
      y += 8;
    });

    doc.save(name + ".pdf");
  } catch {
    ErrorMessage("PDF export failed");
  }
};

// Helper: create a styled worksheet inside an ExcelJS workbook
const _addStyledSheet = (wb, sheetName, cols, rows) => {
  const ws = wb.addWorksheet(sheetName);
  ws.columns = cols.map((c) => ({ header: c.label, key: c.key, width: 22 }));
  const hdr = ws.getRow(1);
  hdr.font = { bold: true, color: { argb: "FFFFFFFF" } };
  hdr.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF1935C" },
  };
  rows.forEach((r) => ws.addRow(r));
};

// Helper: trigger browser download from an ExcelJS workbook
const _saveWorkbook = async (wb, name) => {
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name + ".xlsx";
  a.click();
};

// ALL_LISTING — multi-sheet Excel (Listings + Contracts)
const downloadAllListingExcel = async (listingBank, contractBank, name) => {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    _addStyledSheet(wb, "Listings", CONSUMER_LISTING_COLS, listingBank || []);
    _addStyledSheet(wb, "Contracts", CONTRACT_DETAIL_COLS, contractBank || []);
    await _saveWorkbook(wb, name);
  } catch {
    ErrorMessage("Excel export failed");
  }
};

// PAYMENT_ACTIVITY Contracts tab — multi-sheet Excel
// Sheets: Contracts | Charges | Wallet Activities
// Uses array-based row writes to avoid ExcelJS key-mapping mismatches.
const downloadContractsTabExcel = async (
  transactions,
  walletActivities,
  name,
) => {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    const boolStr = (v) => (v === true ? "Yes" : v === false ? "No" : "");

    const applyHeader = (ws, labels) => {
      const hdr = ws.addRow(labels);
      hdr.font = { bold: true, color: { argb: "FFFFFFFF" } };
      hdr.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF1935C" },
      };
      hdr.alignment = { vertical: "middle" };
    };

    // ── Sheet 1: Contracts ────────────────────────────────────────────────────
    const wsC = wb.addWorksheet("Contracts");
    wsC.columns = CONTRACTS_EXPORT_COLS.map((c) => ({ width: 24 }));
    applyHeader(
      wsC,
      CONTRACTS_EXPORT_COLS.map((c) => c.label),
    );

    transactions.forEach((r) => {
      wsC.addRow([
        r.id,
        getAuditConsumerListing(r),
        getAuditGeneratorListing(r),
        r.status ?? "",
        r.type ?? "",
        r.platform ?? "",
        r.amount ?? "",
        r.volume ?? "",
        r.startDate ?? "",
        r.endDate ?? "",
        r.scheduleDurationDays ?? "",
        r.blockedAmountDurationDays ?? "",
        r.noOfHours ?? "",
        r.paymentType ?? "",
        r.paymentMode ?? "",
        r.transferMode ?? "",
        r.date ?? "",
        r.dateTime ?? "",
        boolStr(r.isScheduled),
        boolStr(r.immediateTransaction),
        r.transactionId ?? "",
        r.againstTransactionId ?? "",
        r.totalCharges ?? "",
        r.totalConsumerCharges ?? "",
        r.totalGeneratorCharges ?? "",
        r.walletAccount?.id ?? "",
        r.walletAccount?.walletAccountType ?? "",
        r.walletAccount?.walletCompanyCode ?? "",
        r.walletAccount?.amountRemaining ?? "",
        r.walletAccount?.amountBlocked ?? "",
        r.remarks ?? "",
      ]);
    });

    // ── Sheet 2: Charges ─────────────────────────────────────────────────────
    const chargeRows = [];
    transactions.forEach((txn) => {
      (txn.walletAccount?.charges || []).forEach((c) => {
        chargeRows.push([
          txn.id,
          txn.name ?? "",
          c.name ?? "",
          c.chargeType ?? "",
          c.type ?? "",
          c.value ?? "",
          c.frequency ?? "",
          c.bearer ?? "",
          c.payer ?? "",
        ]);
      });
    });
    if (chargeRows.length > 0) {
      const wsH = wb.addWorksheet("Charges");
      wsH.columns = CHARGES_EXPORT_COLS.map(() => ({ width: 24 }));
      applyHeader(
        wsH,
        CHARGES_EXPORT_COLS.map((c) => c.label),
      );
      chargeRows.forEach((row) => wsH.addRow(row));
    }

    // ── Sheet 3: Wallet Activities ────────────────────────────────────────────
    if (walletActivities?.length > 0) {
      const wsW = wb.addWorksheet("Wallet Activities");
      wsW.columns = WALLET_EXPORT_COLS.map(() => ({ width: 24 }));
      applyHeader(
        wsW,
        WALLET_EXPORT_COLS.map((c) => c.label),
      );
      walletActivities.forEach((r) => {
        wsW.addRow(WALLET_EXPORT_COLS.map((c) => r[c.key] ?? ""));
      });
    }

    await _saveWorkbook(wb, name);
  } catch {
    ErrorMessage("Excel export failed");
  }
};

// NOCA sheet helper — writes NOCA Documents sheet with merged cells for repeated NOCA fields
const _addNocaSheet = (wb, nocaRows) => {
  const ws = wb.addWorksheet("NOCA Documents");
  ws.columns = NOCA_EXPORT_COLS.map((c) => ({
    header: c.label,
    key: c.key,
    width: 24,
  }));
  const hdr = ws.getRow(1);
  hdr.font = { bold: true, color: { argb: "FFFFFFFF" } };
  hdr.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF1935C" },
  };

  let rowNum = 2;
  nocaRows.forEach((noca) => {
    const txns = noca.bankTransactions ?? [];
    const base = {
      docId: noca.docId ?? "",
      documentType: noca.documentType ?? "",
      referenceType: noca.referenceType ?? "",
      nocaStatus: noca.status ?? "",
    };
    const txnRows =
      txns.length === 0
        ? [
            {
              transactionId: "",
              transactionType: "",
              transferAmount: "",
              fromUser: "",
              toUser: "",
              receiverType: "",
              txnStatus: "",
              txnFor: "",
              txnDateTime: "",
            },
          ]
        : txns.map((txn) => ({
            transactionId: txn.transactionId ?? "",
            transactionType: txn.transactionType ?? "",
            transferAmount: txn.transferAmount ?? "",
            fromUser: txn.fromUser ?? "",
            toUser: txn.toUser ?? "",
            receiverType: txn.receiverType ?? "",
            txnStatus: txn.status ?? "",
            txnFor: txn.txnFor ?? "",
            txnDateTime: txn.txnDateTime ?? "",
          }));

    const startRow = rowNum;
    txnRows.forEach((txnData) => {
      ws.addRow({ ...base, ...txnData });
      rowNum++;
    });

    if (txnRows.length > 1) {
      // Merge repeated NOCA fields (first 4 columns) across all transaction rows
      [1, 2, 3, 4].forEach((col) => {
        ws.mergeCells(startRow, col, rowNum - 1, col);
        ws.getCell(startRow, col).alignment = { vertical: "middle" };
      });
    }
  });
};

const downloadNocaExcel = async (nocaRows, name) => {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    _addNocaSheet(wb, nocaRows);
    await _saveWorkbook(wb, name);
  } catch {
    ErrorMessage("Excel export failed");
  }
};

// Wallet sheet helper — sorts by Contract ID → Listing ID → CP Listing ID
// (empty values sort last), then merges each level hierarchically.
// Empty cells are never merged so blank rows stay visually independent.
const _addWalletSheet = (wb, walletRows) => {
  const ws = wb.addWorksheet("Wallet Transaction");
  ws.columns = WALLET_EXPORT_COLS.map((c) => ({
    header: c.label,
    key: c.key,
    width: 24,
  }));
  const hdr = ws.getRow(1);
  hdr.font = { bold: true, color: { argb: "FFFFFFFF" } };
  hdr.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF1935C" },
  };

  // Non-empty values sort before empty ones; within non-empty, alphabetical
  const cmpNullLast = (a, b) => {
    const av = a ?? "";
    const bv = b ?? "";
    if (av === "" && bv !== "") return 1;
    if (av !== "" && bv === "") return -1;
    return String(av).localeCompare(String(bv));
  };

  const sorted = [...walletRows].sort(
    (a, b) =>
      cmpNullLast(
        a.bilateralContractUniqueId,
        b.bilateralContractUniqueId,
      ) ||
      cmpNullLast(a.listingId, b.listingId) ||
      cmpNullLast(a.counterPartyListingId, b.counterPartyListingId),
  );

  sorted.forEach((r) => ws.addRow(r));

  const colIdx = (key) =>
    WALLET_EXPORT_COLS.findIndex((c) => c.key === key) + 1;

  // Merge consecutive rows in `col` where `keyFn` is the same AND `valueFn`
  // returns a non-empty value (so blank cells are never silently merged away)
  const mergeByKey = (col, keyFn, valueFn) => {
    let groupStart = 2;
    for (let i = 0; i < sorted.length; i++) {
      const excelRow = i + 2;
      const isLast = i === sorted.length - 1;
      const curKey = keyFn(sorted[i]);
      const nextKey = !isLast ? keyFn(sorted[i + 1]) : null;
      if (isLast || curKey !== nextKey) {
        const val = valueFn(sorted[groupStart - 2]);
        if (excelRow > groupStart && val) {
          ws.mergeCells(groupStart, col, excelRow, col);
          ws.getCell(groupStart, col).alignment = { vertical: "middle" };
        }
        groupStart = excelRow + 1;
      }
    }
  };

  // Contract ID — top-level group
  mergeByKey(
    colIdx("bilateralContractUniqueId"),
    (r) => String(r.bilateralContractUniqueId ?? ""),
    (r) => r.bilateralContractUniqueId,
  );

  // Listing ID — grouped within same Contract ID
  mergeByKey(
    colIdx("listingId"),
    (r) =>
      `${r.bilateralContractUniqueId ?? ""}||${r.listingId ?? ""}`,
    (r) => r.listingId,
  );

  // CP Listing ID — grouped within same Contract ID + Listing ID
  mergeByKey(
    colIdx("counterPartyListingId"),
    (r) =>
      `${r.bilateralContractUniqueId ?? ""}||${r.listingId ?? ""}||${r.counterPartyListingId ?? ""}`,
    (r) => r.counterPartyListingId,
  );
};

const downloadWalletExcel = async (walletRows, name) => {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    _addWalletSheet(wb, walletRows);
    await _saveWorkbook(wb, name);
  } catch {
    ErrorMessage("Excel export failed");
  }
};

const downloadAllPaymentExcel = async (
  walletRows,
  transactionRows,
  nocaRows,
  name,
) => {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    _addWalletSheet(wb, walletRows);
    _addStyledSheet(wb, "Contracts", CONTRACTS_EXPORT_COLS, transactionRows);
    _addNocaSheet(wb, nocaRows);
    await _saveWorkbook(wb, name);
  } catch {
    ErrorMessage("Excel export failed");
  }
};

const REVISION_EXPORT_COLS = [
  { key: "revisionId", label: "Revision ID" },
  { key: "revisionDate", label: "Date" },
  { key: "revisionType", label: "Type" },
  { key: "status", label: "Status" },
];

const SLOT_EXPORT_COLS = [
  { key: "dateStartAt", label: "Date Start" },
  { key: "dateEndAt", label: "Date End" },
  { key: "slotStartAt", label: "Slot Start" },
  { key: "slotEndAt", label: "Slot End" },
  { key: "purchaseEnergy", label: "Qty (MW)" },
  // { key: "landingCost", label: "Buy Price (₹)" },
  { key: "energyRateType", label: "Rate Type" },
];

const GEN_SLOT_EXPORT_COLS = [
  { key: "dateStartAt", label: "Date Start" },
  { key: "dateEndAt", label: "Date End" },
  { key: "slotStartAt", label: "Slot Start" },
  { key: "slotEndAt", label: "Slot End" },
  { key: "sellEnergy", label: "Qty (MW)" },
  { key: "sellingCost", label: "Sell Price (₹)" },
  { key: "energyRateType", label: "Rate Type" },
];

const LISTING_INFO_EXPORT_COLS = [
  { key: "capacityId", label: "Capacity ID" },
  { key: "entityType", label: "Entity Type" },
  { key: "name", label: "Listing Name" },
  { key: "status", label: "Status" },
  { key: "listingType", label: "Listing Type" },
  { key: "energySourceType", label: "Energy Source" },
  { key: "energyFuelType", label: "Energy Fuel" },
  { key: "voltageLevel", label: "Voltage Level" },
  { key: "region", label: "Region" },
  { key: "stateLabel", label: "State" },
  { key: "listingExpDate", label: "Listing Exp." },
  { key: "termsExpDate", label: "Terms Exp." },
  { key: "createdAt", label: "Created At" },
  { key: "paymentSecurity", label: "Payment Security" },
  { key: "initialMargin", label: "Initial Margin" },
  { key: "emd", label: "EMD" },
];

const LISTING_CONTRACTS_EXPORT_COLS = [
  { key: "id", label: "ID" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "slotStartAt", label: "Slot Start" },
  { key: "slotEndAt", label: "Slot End" },
  { key: "amount", label: "Amount (₹)" },
  { key: "scheduleDurationDays", label: "Days" },
  { key: "initiatorType", label: "Type" },
  { key: "volume", label: "Volume" },
];

const CONTRACT_INFO_EXPORT_COLS = [
  { key: "id", label: "ID" },
  { key: "contractUniqueId", label: "Contract Unique ID" },
  { key: "status", label: "Status" },
  { key: "schedulingStatus", label: "Scheduling Status" },
  { key: "initiatorType", label: "Initiator Type" },
  { key: "damPrice", label: "DAM Price" },
  { key: "isChargesFinalized", label: "Charges Finalised" },
  { key: "schedulingStartedAt", label: "Scheduling Started" },
  { key: "consumerName", label: "Consumer" },
  { key: "capacityConsumerName", label: "Consumer Listing" },
  { key: "generatorName", label: "Generator" },
  { key: "capacityGeneratorName", label: "Generator Listing" },
  { key: "paymentSecurity", label: "Payment Security" },
  { key: "initialMargin", label: "Initial Margin" },
  { key: "emd", label: "EMD" },
  { key: "conConsentDocId", label: "Con. Consent Doc" },
  { key: "genConsentDocId", label: "Gen. Consent Doc" },
  { key: "conAgreementDocId", label: "Con. Agreement Doc" },
  { key: "genAgreementDocId", label: "Gen. Agreement Doc" },
  { key: "conAgreementAcceptanceStatus", label: "Con. Agreement Acc." },
  { key: "genAgreementAcceptanceStatus", label: "Gen. Agreement Acc." },
  { key: "conTermsAcceptanceStatus", label: "Con. Terms Acc." },
  { key: "genTermsAcceptanceStatus", label: "Gen. Terms Acc." },
];

const BILATERAL_CONN_EXPORT_COLS = [
  { key: "id", label: "ID" },
  { key: "dateStartAt", label: "Date Start" },
  { key: "dateEndAt", label: "Date End" },
  { key: "slotStartAt", label: "Slot Start" },
  { key: "slotEndAt", label: "Slot End" },
  { key: "quantity", label: "Qty (MW)" },
  { key: "amount", label: "Amount (₹)" },
  { key: "energyRateType", label: "Rate Type" },
  { key: "status", label: "Status" },
];

// LISTING_NUMBER — multi-sheet Excel (Listing Info + Slots + Contracts + Revision History)
const downloadListingDetailExcel = async (data, name) => {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    const listing = data?.consumerListing || data?.generatorListing;
    const boolStr = (v) =>
      v === true ? "Yes" : v === false ? "No" : (v ?? "—");

    _addStyledSheet(wb, "Listing Info", LISTING_INFO_EXPORT_COLS, [
      {
        capacityId: data?.capacityId,
        entityType: data?.entityType,
        name: listing?.name,
        status: listing?.status,
        listingType: listing?.listingType,
        energySourceType: listing?.energySourceType,
        energyFuelType: listing?.energyFuelType,
        voltageLevel: listing?.voltageLevel,
        region: listing?.region,
        stateLabel: listing?.stateLabel,
        listingExpDate: listing?.listingExpDate,
        termsExpDate: listing?.termsExpDate,
        createdAt: listing?.createdAt?.split("T")?.[0],
        paymentSecurity: boolStr(listing?.paymentSecurity),
        initialMargin: boolStr(listing?.initialMargin),
        emd: boolStr(listing?.emd),
      },
    ]);

    if (listing?.slots?.length)
      _addStyledSheet(wb, "Slots", SLOT_EXPORT_COLS, listing.slots);

    if (data?.contracts?.length) {
      const contractRows = data.contracts.map((c) => ({
        id: c.id,
        startDate: c.startDate || c.dateStartAt,
        endDate: c.endDate || c.dateEndAt,
        slotStartAt: c.slotStartAt || c.startTime,
        slotEndAt: c.slotEndAt || c.endTime,
        amount: c.amount ?? c.damPrice,
        scheduleDurationDays:
          c.scheduleDurationDays ?? c.blockedAmountDurationDays,
        initiatorType: c.initiatorType ?? c.type,
        volume: c.volume ?? c.quantity,
      }));
      _addStyledSheet(
        wb,
        "Contracts",
        LISTING_CONTRACTS_EXPORT_COLS,
        contractRows,
      );
    }

    if (data?.revisionHistory?.length)
      _addStyledSheet(
        wb,
        "Revision History",
        REVISION_EXPORT_COLS,
        data.revisionHistory,
      );

    await _saveWorkbook(wb, name);
  } catch {
    ErrorMessage("Excel export failed");
  }
};

// CONTRACT_ID — multi-sheet Excel (Contract Info + Consumer Slots + Generator Slots + Bilateral Connections + Revision History)
const downloadContractDetailExcel = async (data, name) => {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    const contract = data?.contract;
    const boolStr = (v) =>
      v === true ? "Yes" : v === false ? "No" : (v ?? "—");

    if (contract) {
      _addStyledSheet(wb, "Contract Info", CONTRACT_INFO_EXPORT_COLS, [
        {
          id: contract.id,
          contractUniqueId: contract.contractUniqueId,
          status: contract.status,
          schedulingStatus: contract.schedulingStatus,
          initiatorType: contract.initiatorType,
          damPrice: contract.damPrice,
          isChargesFinalized: boolStr(contract.isChargesFinalized),
          schedulingStartedAt: contract.schedulingStartedAt
            ?.replace("T", " ")
            ?.slice(0, 19),
          consumerName: contract.consumerName,
          capacityConsumerName: contract.capacityConsumerName,
          generatorName: contract.generatorName,
          capacityGeneratorName: contract.capacityGeneratorName,
          paymentSecurity: contract.paymentSecurity,
          initialMargin: contract.initialMargin,
          emd: contract.emd,
          conConsentDocId: contract.conConsentDocId,
          genConsentDocId: contract.genConsentDocId,
          conAgreementDocId: contract.conAgreementDocId,
          genAgreementDocId: contract.genAgreementDocId,
          conAgreementAcceptanceStatus: boolStr(
            contract.conAgreementAcceptanceStatus,
          ),
          genAgreementAcceptanceStatus: boolStr(
            contract.genAgreementAcceptanceStatus,
          ),
          conTermsAcceptanceStatus: boolStr(contract.conTermsAcceptanceStatus),
          genTermsAcceptanceStatus: boolStr(contract.genTermsAcceptanceStatus),
        },
      ]);
    }

    const con = contract?.capacityConsumer;
    const gen = contract?.capacityGenerator;
    if (con?.slots?.length)
      _addStyledSheet(wb, "Consumer Slots", SLOT_EXPORT_COLS, con.slots);
    if (gen?.slots?.length)
      _addStyledSheet(wb, "Generator Slots", GEN_SLOT_EXPORT_COLS, gen.slots);

    const connections = contract?.bilateralConnections || [];
    if (connections.length)
      _addStyledSheet(
        wb,
        "Bilateral Connections",
        BILATERAL_CONN_EXPORT_COLS,
        connections,
      );

    if (data?.revisionHistory?.length)
      _addStyledSheet(
        wb,
        "Revision History",
        REVISION_EXPORT_COLS,
        data.revisionHistory,
      );

    await _saveWorkbook(wb, name);
  } catch {
    ErrorMessage("Excel export failed");
  }
};

// ─── Status Chip ──────────────────────────────────────────────────────────────
const STATUS_MAP = {
  SUCCESS: { bg: "#f0fdf4", color: "#166534", dot: "#22c55e" },
  COMPLETED: { bg: "#f0fdf4", color: "#166534", dot: "#22c55e" },
  RELEASED: { bg: "#f0fdf4", color: "#166534", dot: "#22c55e" },
  CREDITED: { bg: "#f0fdf4", color: "#166534", dot: "#22c55e" },
  SETTLED: { bg: "#f0fdf4", color: "#166534", dot: "#22c55e" },
  FAILED: { bg: "#fef2f2", color: "#991b1b", dot: "#ef4444" },
  CANCELLED: { bg: "#fef2f2", color: "#991b1b", dot: "#ef4444" },
  REJECTED: { bg: "#fef2f2", color: "#991b1b", dot: "#ef4444" },
  PENDING: { bg: "#fffbeb", color: "#92400e", dot: "#f59e0b" },
  BLOCKED: { bg: "#fff7ed", color: "#c2410c", dot: "#f97316" },
  DEBITED: { bg: "#fff7ed", color: "#c2410c", dot: "#f97316" },
  PROCESSING: { bg: "#eff6ff", color: "#1e40af", dot: "#3b82f6" },
  INITIATED: { bg: "#eff6ff", color: "#1e40af", dot: "#3b82f6" },
  STORED: { bg: "#eff6ff", color: "#1e40af", dot: "#3b82f6" },
  REFUNDED: { bg: "#f5f3ff", color: "#5b21b6", dot: "#8b5cf6" },
  EXPIRED: { bg: "#f1f5f9", color: "#64748b", dot: "#94a3b8" },
};

const StatusChip = ({ value }) => {
  if (!value) return <span>—</span>;
  const cfg = STATUS_MAP[String(value).toUpperCase()] || {
    bg: "#f3f4f6",
    color: "#6b7280",
    dot: "#9ca3af",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 8px",
        borderRadius: 12,
        background: cfg.bg,
        color: cfg.color,
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {value}
    </span>
  );
};

// Parses "C404_MAR10_MAR10" → {entityType:"CONSUMER",capacityId:404}
//         "G405_MAR10_MAR10" → {entityType:"GENERATOR",capacityId:405}
const parseListingNumber = (str) => {
  const m = str.trim().match(/^([CcGg])(\d+)/);
  if (!m) return null;
  return {
    entityType: m[1].toUpperCase() === "C" ? "CONSUMER" : "GENERATOR",
    capacityId: Number(m[2]),
  };
};

// ─── Payment tab config ───────────────────────────────────────────────────────
const SEC_PAGE_SIZE = 20;

const TAB_CONFIG = [
  {
    label: "Wallet Transaction",
    accent: "#f97316",
    light: "#fff7ed",
    badge: "#c2410c",
    badgeBg: "#ffedd5",
    cols: PAYMENT_COLS,
    chipKeys: ["activity"],
    exportKey: "wallet",
  },
  {
    label: "Contracts",
    accent: "#3b82f6",
    light: "#eff6ff",
    badge: "#1d4ed8",
    badgeBg: "#dbeafe",
    cols: TRANSACTION_COLS,
    chipKeys: ["status"],
    exportKey: "transactions",
  },
  {
    label: "NOCA Documents",
    accent: "#8b5cf6",
    light: "#f5f3ff",
    badge: "#6d28d9",
    badgeBg: "#ede9fe",
    cols: NOCA_COLS,
    chipKeys: ["status"],
    exportKey: "noca",
  },
];

const fmtSlotTime = (v) => {
  if (!v) return "—";
  const t = String(v).slice(0, 5);
  return t === "23:59" ? "24:00" : t;
};

const fmtAmount = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Math.round(n),
  );
};

// ─── Payment Type Chip ───────────────────────────────────────────────────────
const PAYMENT_TYPE_COLORS = {
  FUNDS_PAYIN: { bg: "#f0fdf4", color: "#166534", label: "Funds Pay-In" },
  INITIAL_MARGIN: { bg: "#eff6ff", color: "#1d4ed8", label: "Initial Margin" },
  INITIAL_OBLIGATION: {
    bg: "#f5f3ff",
    color: "#6d28d9",
    label: "Initial Obligation",
  },
  FINAL_OBLIGATION: {
    bg: "#ecfdf5",
    color: "#065f46",
    label: "Final Obligation",
  },
  EMD: { bg: "#fff7ed", color: "#c2410c", label: "EMD" },
  SETTLEMENT: { bg: "#f0fdf4", color: "#166534", label: "Settlement" },
  REFUND: { bg: "#fef2f2", color: "#991b1b", label: "Refund" },
};

const PaymentTypeChip = ({ value }) => {
  if (!value) return <span style={{ color: "#94a3b8" }}>—</span>;
  const cfg = PAYMENT_TYPE_COLORS[value] || { bg: "#f1f5f9", color: "#475569" };
  const label =
    cfg.label ||
    value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 9px",
        borderRadius: 10,
        background: cfg.bg,
        color: cfg.color,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

// ─── Transaction Detail Modal ─────────────────────────────────────────────────
const CHILD_COLS = [
  { key: "id", label: "ID" },
  { key: "date", label: "Date" },
  { key: "amount", label: "Amount (₹)" },
  { key: "volume", label: "Volume (MWh)" },
  { key: "status", label: "Status" },
  { key: "paymentStatus", label: "Payment Status" },
  { key: "amountBlocked", label: "Blocked (₹)" },
  { key: "chargesApplied", label: "Charges (₹)" },
  { key: "noOfHours", label: "Hours" },
  { key: "scheduleDay", label: "Schedule Day" },
  { key: "progress", label: "Progress (%)" },
];

const DetailRow = ({ label, value }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 13,
        color: "#1e293b",
        fontWeight: 500,
        wordBreak: "break-word",
      }}
    >
      {value ?? "—"}
    </span>
  </div>
);

const TransactionDetailModal = ({ txn, onClose }) => {
  if (!txn) return null;

  const wallet = txn.walletAccount || {};
  const children = txn.childTransactions || [];

  const infoSections = [
    {
      title: "Transaction Overview",
      accent: "#f97316",
      fields: [
        { label: "ID", value: txn.id },
        { label: "Name", value: txn.name },
        { label: "Status", value: <StatusChip value={txn.status} /> },
        { label: "Type", value: txn.type },
        {
          label: "Amount (₹)",
          value: txn.amount != null ? `₹${fmtAmount(txn.amount)}` : "—",
        },
        { label: "Volume (MWh)", value: txn.volume },
        { label: "Platform", value: txn.platform },
      ],
    },
    {
      title: "Payment Details",
      accent: "#3b82f6",
      fields: [
        { label: "Payment Mode", value: txn.paymentMode },
        { label: "Transfer Mode", value: txn.transferMode },
        { label: "Date", value: txn.date },
        { label: "Scheduled", value: txn.isScheduled ? "Yes" : "No" },
        { label: "Immediate", value: txn.immediateTransaction ? "Yes" : "No" },
        { label: "No. of Hours", value: txn.noOfHours },
      ],
    },
    {
      title: "Contract Details",
      accent: "#8b5cf6",
      fields: [
        { label: "Start Date", value: txn.startDate },
        { label: "End Date", value: txn.endDate },
        { label: "Against Transaction ID", value: txn.againstTransactionId },
        { label: "Consumer Listing", value: getAuditConsumerListing(txn) },
        {
          label: "Against Generator Listing",
          value: getAuditGeneratorListing(txn),
        },
        { label: "Schedule Duration (days)", value: txn.scheduleDurationDays },
        {
          label: "Blocked Amount Duration",
          value: txn.blockedAmountDurationDays,
        },
      ],
    },
    {
      title: "Charges",
      accent: "#22c55e",
      fields: [
        {
          label: "Total Charges (₹)",
          value:
            txn.totalCharges != null ? `₹${fmtAmount(txn.totalCharges)}` : "—",
        },
        {
          label: "Total Consumer Charges (₹)",
          value:
            txn.totalConsumerCharges != null
              ? `₹${fmtAmount(txn.totalConsumerCharges)}`
              : "—",
        },
        {
          label: "Total Generator Charges (₹)",
          value:
            txn.totalGeneratorCharges != null
              ? `₹${fmtAmount(txn.totalGeneratorCharges)}`
              : "—",
        },
      ],
    },
    {
      title: "Wallet Account",
      accent: "#f59e0b",
      fields: [
        { label: "Wallet ID", value: wallet.id },
        { label: "Type", value: wallet.walletAccountType },
        { label: "Company Code", value: wallet.walletCompanyCode },
        {
          label: "Amount Remaining (₹)",
          value:
            wallet.amountRemaining != null
              ? `₹${fmtAmount(wallet.amountRemaining)}`
              : "—",
        },
        {
          label: "Amount Blocked (₹)",
          value:
            wallet.amountBlocked != null
              ? `₹${fmtAmount(wallet.amountBlocked)}`
              : "—",
        },
      ],
    },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "min(960px, 96vw)",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 24px",
            borderBottom: "1px solid #f1f5f9",
            flexShrink: 0,
            background: "#fff",
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>
              Transaction Details
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
              ID: {txn.id} · {txn.name}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "#f1f5f9",
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
            }}
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 24 }}>
          {/* Info sections */}
          {infoSections.map((sec) => (
            <div
              key={sec.title}
              style={{
                border: `1px solid ${sec.accent}30`,
                borderRadius: 12,
                overflow: "hidden",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  padding: "10px 16px",
                  background: `${sec.accent}12`,
                  borderBottom: `1px solid ${sec.accent}25`,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: sec.accent,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {sec.title}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: "14px 20px",
                  padding: "14px 16px",
                }}
              >
                {sec.fields.map((f) => (
                  <DetailRow key={f.label} label={f.label} value={f.value} />
                ))}
              </div>
            </div>
          ))}

          {/* Child Transactions */}
          <div
            style={{
              border: "1px solid #3b82f630",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "10px 16px",
                background: "#eff6ff",
                borderBottom: "1px solid #3b82f625",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#1d4ed8",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Child Transactions
              </span>
              <span
                style={{
                  background: "#dbeafe",
                  color: "#1d4ed8",
                  borderRadius: 20,
                  padding: "1px 9px",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {children.length}
              </span>
            </div>

            {children.length === 0 ? (
              <div
                style={{
                  padding: 20,
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: 13,
                }}
              >
                No child transactions
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr>
                      {CHILD_COLS.map((col) => (
                        <th
                          key={col.key}
                          style={{
                            padding: "9px 14px",
                            background: "#f8fafc",
                            borderBottom: "2px solid #3b82f6",
                            textAlign:
                              col.key === "amount" ||
                              col.key === "amountBlocked" ||
                              col.key === "chargesApplied"
                                ? "right"
                                : "left",
                            fontWeight: 600,
                            color: "#64748b",
                            whiteSpace: "nowrap",
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {children.map((child, i) => (
                      <tr
                        key={child.id ?? i}
                        style={{
                          background: i % 2 === 0 ? "#fff" : "#f8fafc",
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        {CHILD_COLS.map((col) => {
                          const val = child[col.key];
                          const isAmt =
                            col.key === "amount" ||
                            col.key === "amountBlocked" ||
                            col.key === "chargesApplied";
                          const isStatus =
                            col.key === "status" || col.key === "paymentStatus";
                          return (
                            <td
                              key={col.key}
                              style={{
                                padding: "8px 14px",
                                whiteSpace: "nowrap",
                                textAlign: isAmt ? "right" : "left",
                                color: "#334155",
                                fontSize: 12,
                                verticalAlign: "middle",
                              }}
                            >
                              {isStatus ? (
                                <StatusChip value={val} />
                              ) : isAmt ? (
                                <span
                                  style={{
                                    fontWeight: 600,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {val != null ? `₹${fmtAmount(val)}` : "—"}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: val != null ? "#334155" : "#94a3b8",
                                  }}
                                >
                                  {val ?? "—"}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Listing Detail Modal ─────────────────────────────────────────────────────
const SLOT_COLS = [
  { key: "dateStartAt", label: "Start Date" },
  { key: "dateEndAt", label: "End Date" },
  { key: "slotStartAt", label: "Slot Start" },
  { key: "slotEndAt", label: "Slot End" },
  { key: "energyRateStr", label: "Rate" },
  { key: "energyRateType", label: "Rate Type" },
  { key: "purchaseEnergy", label: "Purchase (MW)" },
  { key: "requiredQty", label: "Required (MW)" },
  { key: "minimumQty", label: "Min Qty (MW)" },
  // { key: "landingCost", label: "Landing Cost" },
];

const ListingDetailModal = ({ listing, contracts = [], onClose }) => {
  if (!listing) return null;
  const slots = listing.slots || [];

  const infoSections = [
    {
      title: "Listing Overview",
      accent: "#f97316",
      fields: [
        { label: "ID", value: listing.id },
        { label: "Name", value: listing.name },
        { label: "Status", value: <StatusChip value={listing.status} /> },
        { label: "Price Discovery", value: listing.priceDiscoveryType },
        {
          label: "Listing Type",
          value: listing.listingType?.replace(/_/g, " "),
        },
        { label: "Delivery Type", value: listing.deliveryType || "—" },
        { label: "Consumer", value: listing.consumerName || listing.conName },
        {
          label: "Created At",
          value: listing.createdAt?.split("T")?.join(" ")?.slice(0, 19),
        },
      ],
    },
    {
      title: "Location & Technical",
      accent: "#8b5cf6",
      fields: [
        { label: "Region", value: listing.region },
        { label: "State", value: listing.stateLabel },
        { label: "Voltage Level", value: listing.voltageLevel },
        { label: "Energy Source", value: listing.energySourceType },
        { label: "Energy Fuel", value: listing.energyFuelType },
        { label: "Interstate", value: listing.isInterstate ? "Yes" : "No" },
        { label: "Bilateral", value: listing.isBilateral ? "Yes" : "No" },
        { label: "Intra Day", value: listing.isIntraDay ? "Yes" : "No" },
      ],
    },
    {
      title: "Expiry & Terms",
      accent: "#3b82f6",
      fields: [
        { label: "Listing Exp. Date", value: listing.listingExpDate },
        { label: "Listing Exp. Time", value: listing.listingExpTime },
        { label: "Terms Exp. Date", value: listing.termsExpDate },
        { label: "Terms Exp. Time", value: listing.termsExpTime },
        {
          label: "Listing Expired",
          value: listing.isListingExpired ? "Yes" : "No",
        },
        {
          label: "Terms Expired",
          value: listing.isTermsExpired ? "Yes" : "No",
        },
      ],
    },
    {
      title: "Financial",
      accent: "#22c55e",
      fields: [
        { label: "Initial Margin (%)", value: listing.initialMargin },
        {
          label: "EMD (₹)",
          value: listing.emd != null ? `₹${fmtAmount(listing.emd)}` : "—",
        },
        { label: "Payment Security", value: listing.paymentSecurity },
        { label: "Matching Tags", value: listing.matchingTags || "—" },
        { label: "Comment", value: listing.comment || "—" },
      ],
    },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "min(960px, 96vw)",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 24px",
            borderBottom: "1px solid #f1f5f9",
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>
              Listing Details
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
              ID: {listing.id} · {listing.name}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "#f1f5f9",
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
            }}
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 24 }}>
          {infoSections.map((sec) => (
            <div
              key={sec.title}
              style={{
                border: `1px solid ${sec.accent}30`,
                borderRadius: 12,
                overflow: "hidden",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  padding: "10px 16px",
                  background: `${sec.accent}12`,
                  borderBottom: `1px solid ${sec.accent}25`,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: sec.accent,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {sec.title}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: "14px 20px",
                  padding: "14px 16px",
                }}
              >
                {sec.fields.map((f) => (
                  <DetailRow key={f.label} label={f.label} value={f.value} />
                ))}
              </div>
            </div>
          ))}

          {/* Slots */}
          <div
            style={{
              border: "1px solid #f97316" + "30",
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                padding: "10px 16px",
                background: "#fff7ed",
                borderBottom: "1px solid #f9733625",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#c2410c",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Slots
              </span>
              <span
                style={{
                  background: "#ffedd5",
                  color: "#c2410c",
                  borderRadius: 20,
                  padding: "1px 9px",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {slots.length}
              </span>
            </div>
            {slots.length === 0 ? (
              <div
                style={{
                  padding: 20,
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: 13,
                }}
              >
                No slots
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr>
                      {SLOT_COLS.map((col) => (
                        <th
                          key={col.key}
                          style={{
                            padding: "9px 14px",
                            background: "#f8fafc",
                            borderBottom: "2px solid #f97316",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#64748b",
                            whiteSpace: "nowrap",
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map((slot, i) => (
                      <tr
                        key={slot.id ?? i}
                        style={{
                          background: i % 2 === 0 ? "#fff" : "#f8fafc",
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        {SLOT_COLS.map((col) => {
                          const val = slot[col.key];
                          const isTime = col.key === "slotStartAt" || col.key === "slotEndAt";
                          const display = isTime ? fmtSlotTime(val) : (val ?? "—");
                          return (
                            <td
                              key={col.key}
                              style={{
                                padding: "8px 14px",
                                whiteSpace: "nowrap",
                                color: val != null ? "#334155" : "#94a3b8",
                                fontSize: 12,
                                verticalAlign: "middle",
                              }}
                            >
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Contract Details */}
          {contracts.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              {/* Section heading */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#6d28d9",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Contracts
                </span>
                <span
                  style={{
                    background: "#ede9fe",
                    color: "#6d28d9",
                    borderRadius: 20,
                    padding: "1px 9px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {contracts.length}
                </span>
              </div>

              {/* One card per contract */}
              {contracts.map((c, i) => (
                <div
                  key={c.id ?? i}
                  style={{
                    border: "1px solid #8b5cf630",
                    borderRadius: 12,
                    overflow: "hidden",
                    marginBottom: 12,
                  }}
                >
                  {/* Card header */}
                  <div
                    style={{
                      padding: "10px 16px",
                      background: "#f5f3ff",
                      borderBottom: "1px solid #8b5cf620",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#6d28d9",
                        fontFamily: "monospace",
                      }}
                    >
                      {c.contractUniqueId || `#${c.id}`}
                    </span>
                    <StatusChip value={c.status} />
                    {c.schedulingStatus && c.schedulingStatus !== c.status && (
                      <StatusChip value={c.schedulingStatus} />
                    )}
                    {c.initiatorType && (
                      <span
                        style={{
                          fontSize: 11,
                          background: "#fff7ed",
                          color: "#c2410c",
                          border: "1px solid #fed7aa",
                          borderRadius: 20,
                          padding: "2px 10px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {c.initiatorType}
                      </span>
                    )}
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 11,
                        color: "#94a3b8",
                      }}
                    >
                      {c.consumerName} ↔ {c.generatorName}
                    </span>
                  </div>

                  {/* Contract Overview grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                      gap: "14px 20px",
                      padding: "14px 16px",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <DetailRow label="Consumer" value={c.consumerName} />
                    <DetailRow label="Generator" value={c.generatorName} />
                    <DetailRow label="Consumer Listing" value={c.capacityConsumerName} />
                    <DetailRow label="Generator Listing" value={c.capacityGeneratorName} />
                    <DetailRow label="Scheduling Status" value={c.schedulingStatus} />
                    <DetailRow
                      label="DAM Price"
                      value={c.damPrice != null ? `₹${c.damPrice}` : "—"}
                    />
                    <DetailRow
                      label="Charges Finalized"
                      value={
                        c.isChargesFinalized != null
                          ? c.isChargesFinalized ? "Yes" : "No"
                          : "—"
                      }
                    />
                  </div>

                  {/* Financial summary */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                      gap: "14px 20px",
                      padding: "14px 16px",
                      background: "#fafafa",
                    }}
                  >
                    <DetailRow
                      label="Initial Margin (%)"
                      value={c.initialMargin != null ? `${c.initialMargin}%` : "—"}
                    />
                    <DetailRow
                      label="EMD (₹)"
                      value={c.emd != null ? `₹${fmtAmount(c.emd)}` : "—"}
                    />
                    <DetailRow label="Payment Security" value={c.paymentSecurity} />
                    <DetailRow
                      label="Con Agreement"
                      value={
                        c.conAgreementAcceptanceStatus != null
                          ? c.conAgreementAcceptanceStatus ? "Accepted" : "Pending"
                          : "—"
                      }
                    />
                    <DetailRow
                      label="Gen Agreement"
                      value={
                        c.genAgreementAcceptanceStatus != null
                          ? c.genAgreementAcceptanceStatus ? "Accepted" : "Pending"
                          : "—"
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Contract Detail Modal ────────────────────────────────────────────────────
const CONTRACT_TABS = [
  {
    key: "contract",
    label: "Contract Details",
    accent: "#f97316",
    light: "#fff7ed",
    badge: "#c2410c",
  },
  {
    key: "connection",
    label: "Connection Details",
    accent: "#3b82f6",
    light: "#eff6ff",
    badge: "#1d4ed8",
  },
  {
    key: "listings",
    label: "Listing Details",
    accent: "#8b5cf6",
    light: "#f5f3ff",
    badge: "#6d28d9",
  },
];

const ContractDetailModal = ({ contract, onClose }) => {
  const [activeTab, setActiveTab] = React.useState("contract");
  if (!contract) return null;

  const consumerListing =
    contract.capacityConsumer || contract.consumerListing || null;
  const generatorListing =
    contract.capacityGenerator || contract.generatorListing || null;
  const connections = contract.bilateralConnections || [];

  const tab = CONTRACT_TABS.find((t) => t.key === activeTab);

  const renderContractTab = () => (
    <>
      <div
        style={{
          border: "1px solid #f9733630",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            padding: "10px 16px",
            background: "#fff7ed",
            borderBottom: "1px solid #f9733625",
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#c2410c",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Contract Overview
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "14px 20px",
            padding: "14px 16px",
          }}
        >
          <DetailRow
            label="Contract ID"
            value={
              contract.contractUniqueId ||
              contract.bilateralContractUniqueId ||
              contract.id
            }
          />
          <DetailRow
            label="Status"
            value={<StatusChip value={contract.status} />}
          />
          <DetailRow label="Consumer" value={contract.consumerName} />
          <DetailRow label="Generator" value={contract.generatorName} />
          <DetailRow
            label="Consumer Listing"
            value={contract.capacityConsumerName}
          />
          <DetailRow
            label="Generator Listing"
            value={contract.capacityGeneratorName}
          />
          <DetailRow
            label="Scheduling Status"
            value={contract.schedulingStatus}
          />
          <DetailRow label="Initiator" value={contract.initiatorType} />
          <DetailRow
            label="DAM Price"
            value={contract.damPrice != null ? `₹${contract.damPrice}` : "—"}
          />
          <DetailRow
            label="Charges Finalized"
            value={
              contract.isChargesFinalized != null
                ? contract.isChargesFinalized
                  ? "Yes"
                  : "No"
                : "—"
            }
          />
        </div>
      </div>
      <div
        style={{
          border: "1px solid #22c55e30",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            padding: "10px 16px",
            background: "#f0fdf4",
            borderBottom: "1px solid #22c55e25",
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#166534",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Financial Summary
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "14px 20px",
            padding: "14px 16px",
          }}
        >
          <DetailRow
            label="Initial Margin (%)"
            value={
              contract.initialMargin != null
                ? `${contract.initialMargin}%`
                : "—"
            }
          />
          <DetailRow
            label="EMD (₹)"
            value={contract.emd != null ? `₹${fmtAmount(contract.emd)}` : "—"}
          />
          <DetailRow
            label="Payment Security"
            value={contract.paymentSecurity}
          />
          <DetailRow
            label="Transaction ID"
            value={contract.transactionId || "—"}
          />
          <DetailRow
            label="Con Agreement"
            value={
              contract.conAgreementAcceptanceStatus != null
                ? contract.conAgreementAcceptanceStatus
                  ? "Accepted"
                  : "Pending"
                : "—"
            }
          />
          <DetailRow
            label="Gen Agreement"
            value={
              contract.genAgreementAcceptanceStatus != null
                ? contract.genAgreementAcceptanceStatus
                  ? "Accepted"
                  : "Pending"
                : "—"
            }
          />
        </div>
      </div>
      {contract.charges?.length > 0 && (
        <div
          style={{
            border: "1px solid #f59e0b30",
            borderRadius: 12,
            overflow: "hidden",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              padding: "10px 16px",
              background: "#fffbeb",
              borderBottom: "1px solid #f59e0b25",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#92400e",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Charges
            </span>
            <span
              style={{
                background: "#fef3c7",
                color: "#92400e",
                borderRadius: 20,
                padding: "1px 9px",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {contract.charges.length}
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  {[
                    "Name",
                    "Charge Type",
                    "Type",
                    "Value",
                    "Frequency",
                    "Bearer",
                    "Payer",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "8px 14px",
                        background: "#f8fafc",
                        borderBottom: "2px solid #f59e0b",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#64748b",
                        whiteSpace: "nowrap",
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contract.charges.map((c, i) => (
                  <tr
                    key={c.id ?? i}
                    style={{
                      background: i % 2 === 0 ? "#fff" : "#f8fafc",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {c.name ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {c.chargeType ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {c.type ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                        fontWeight: 600,
                      }}
                    >
                      {c.value ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {c.frequency ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {c.bearer ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {c.payer ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  const renderConnectionTab = () => (
    <>
      <div
        style={{
          border: "1px solid #3b82f630",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            padding: "10px 16px",
            background: "#eff6ff",
            borderBottom: "1px solid #3b82f625",
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#1d4ed8",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Technical Details
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "14px 20px",
            padding: "14px 16px",
          }}
        >
          <DetailRow
            label="Region"
            value={consumerListing?.region || generatorListing?.region}
          />
          <DetailRow
            label="Voltage Level"
            value={
              consumerListing?.voltageLevel || generatorListing?.voltageLevel
            }
          />
          <DetailRow
            label="Delivery Type"
            value={
              consumerListing?.deliveryType || generatorListing?.deliveryType
            }
          />
          <DetailRow
            label="State"
            value={consumerListing?.stateLabel || generatorListing?.stateLabel}
          />
          <DetailRow
            label="Interstate"
            value={
              consumerListing?.isInterstate != null
                ? consumerListing.isInterstate
                  ? "Yes"
                  : "No"
                : "—"
            }
          />
          <DetailRow
            label="Ex-Bus Bar"
            value={
              consumerListing?.isExBusBar != null
                ? consumerListing.isExBusBar
                  ? "Yes"
                  : "No"
                : "—"
            }
          />
          <DetailRow
            label="Intra Day"
            value={
              consumerListing?.isIntraDay != null
                ? consumerListing.isIntraDay
                  ? "Yes"
                  : "No"
                : "—"
            }
          />
          <DetailRow
            label="Energy Source"
            value={
              consumerListing?.energySourceType ||
              generatorListing?.energySourceType
            }
          />
          <DetailRow
            label="Energy Fuel"
            value={
              consumerListing?.energyFuelType ||
              generatorListing?.energyFuelType
            }
          />
          <DetailRow
            label="Payment Config"
            value={
              consumerListing?.paymentConfigurationId ||
              generatorListing?.paymentConfigurationId
            }
          />
        </div>
      </div>

      {/* Bilateral Connections table */}
      <div
        style={{
          border: "1px solid #3b82f630",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            padding: "10px 16px",
            background: "#eff6ff",
            borderBottom: "1px solid #3b82f625",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#1d4ed8",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Bilateral Connections
          </span>
          <span
            style={{
              background: "#dbeafe",
              color: "#1d4ed8",
              borderRadius: 20,
              padding: "1px 9px",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {connections.length}
          </span>
        </div>
        {connections.length === 0 ? (
          <div
            style={{
              padding: 20,
              textAlign: "center",
              color: "#94a3b8",
              fontSize: 13,
            }}
          >
            No connections
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  {[
                    "ID",
                    "Start Date",
                    "End Date",
                    "Slot Start",
                    "Slot End",
                    "Quantity (MW)",
                    "Amount",
                    "Rate Type",
                    "Status",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "8px 14px",
                        background: "#f8fafc",
                        borderBottom: "2px solid #3b82f6",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#64748b",
                        whiteSpace: "nowrap",
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {connections.map((conn, i) => (
                  <tr
                    key={conn.id ?? i}
                    style={{
                      background: i % 2 === 0 ? "#fff" : "#f8fafc",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {conn.id ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {conn.dateStartAt ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {conn.dateEndAt ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {fmtSlotTime(conn.slotStartAt)}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {fmtSlotTime(conn.slotEndAt)}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                        fontWeight: 600,
                      }}
                    >
                      {conn.quantity ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                        fontWeight: 600,
                      }}
                    >
                      {conn.amount ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "7px 14px",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      {conn.energyRateType ?? "—"}
                    </td>
                    <td style={{ padding: "7px 14px", fontSize: 12 }}>
                      <StatusChip value={conn.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  const renderListingSection = (listing, title, accent) => {
    if (!listing)
      return (
        <div
          style={{
            border: `1px solid ${accent}30`,
            borderRadius: 12,
            overflow: "hidden",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              padding: "10px 16px",
              background: `${accent}12`,
              borderBottom: `1px solid ${accent}25`,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: accent,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {title}
            </span>
          </div>
          <div
            style={{
              padding: 20,
              textAlign: "center",
              color: "#94a3b8",
              fontSize: 13,
            }}
          >
            No data available
          </div>
        </div>
      );
    const slots = listing.slots || [];
    return (
      <div
        style={{
          border: `1px solid ${accent}30`,
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            padding: "10px 16px",
            background: `${accent}12`,
            borderBottom: `1px solid ${accent}25`,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: accent,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {title}
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "14px 20px",
            padding: "14px 16px",
          }}
        >
          <DetailRow label="ID" value={listing.id} />
          <DetailRow label="Name" value={listing.name} />
          <DetailRow
            label="Status"
            value={<StatusChip value={listing.status} />}
          />
          <DetailRow
            label="Price Discovery"
            value={listing.priceDiscoveryType}
          />
          <DetailRow
            label="Listing Type"
            value={listing.listingType?.replace(/_/g, " ")}
          />
          <DetailRow label="Region" value={listing.region} />
          <DetailRow label="Voltage Level" value={listing.voltageLevel} />
          <DetailRow
            label="Initial Margin"
            value={
              listing.initialMargin != null ? `${listing.initialMargin}%` : "—"
            }
          />
          <DetailRow
            label="EMD (₹)"
            value={listing.emd != null ? `₹${fmtAmount(listing.emd)}` : "—"}
          />
          <DetailRow
            label="Listing Exp."
            value={
              listing.listingExpDate
                ? `${listing.listingExpDate} ${listing.listingExpTime || ""}`.trim()
                : "—"
            }
          />
          <DetailRow
            label="Terms Exp."
            value={
              listing.termsExpDate
                ? `${listing.termsExpDate}   ${listing.termsExpTime || ""}`.trim()
                : "—"
            }
          />
          <DetailRow
            label="Created At"
            value={listing.createdAt?.split("T")?.[0]}
          />
        </div>
        {slots.length > 0 && (
          <>
            <div
              style={{
                padding: "8px 16px",
                borderTop: `1px solid ${accent}20`,
                background: `${accent}08`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: accent,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Slots
              </span>
              <span
                style={{
                  background: `${accent}20`,
                  color: accent,
                  borderRadius: 20,
                  padding: "1px 8px",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {slots.length}
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr>
                    {SLOT_COLS.map((col) => (
                      <th
                        key={col.key}
                        style={{
                          padding: "8px 14px",
                          background: "#f8fafc",
                          borderBottom: `2px solid ${accent}`,
                          textAlign: "left",
                          fontWeight: 600,
                          color: "#64748b",
                          whiteSpace: "nowrap",
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {slots.map((slot, i) => (
                    <tr
                      key={slot.id ?? i}
                      style={{
                        background: i % 2 === 0 ? "#fff" : "#f8fafc",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      {SLOT_COLS.map((col) => (
                        <td
                          key={col.key}
                          style={{
                            padding: "7px 14px",
                            whiteSpace: "nowrap",
                            color:
                              slot[col.key] != null ? "#334155" : "#94a3b8",
                            fontSize: 12,
                            verticalAlign: "middle",
                          }}
                        >
                          {slot[col.key] ?? "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderListingsTab = () => (
    <>
      {renderListingSection(
        consumerListing,
        `Consumer Listing${consumerListing?.name ? ` — ${consumerListing.name}` : ""}`,
        "#3b82f6",
      )}
      {renderListingSection(
        generatorListing,
        `Generator Listing${generatorListing?.name ? ` — ${generatorListing.name}` : ""}`,
        "#8b5cf6",
      )}
    </>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "min(1000px, 96vw)",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: "1px solid #f1f5f9",
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>
              Contract Details
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
              {contract.contractUniqueId ||
                contract.bilateralContractUniqueId ||
                `ID: ${contract.id}`}{" "}
              · {contract.consumerName} ↔ {contract.generatorName}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "#f1f5f9",
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
            }}
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </button>
        </div>

        {/* Tab bar */}
        <div
          style={{
            display: "flex",
            borderBottom: "2px solid #f1f5f9",
            background: "#fff",
            flexShrink: 0,
          }}
        >
          {CONTRACT_TABS.map((t) => {
            const active = t.key === activeTab;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "12px 20px",
                  border: "none",
                  borderBottom: `2px solid ${active ? t.accent : "transparent"}`,
                  marginBottom: "-2px",
                  background: active ? t.light : "transparent",
                  color: active ? t.badge : "#64748b",
                  fontWeight: active ? 700 : 500,
                  cursor: "pointer",
                  fontSize: 13,
                  transition: "all 0.15s",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 24 }}>
          {activeTab === "contract" && renderContractTab()}
          {activeTab === "connection" && renderConnectionTab()}
          {activeTab === "listings" && renderListingsTab()}
        </div>
      </div>
    </div>
  );
};

// ─── Payment Table (tab content) ─────────────────────────────────────────────
const NocaAccordionTable = ({
  rows,
  accent = "#8b5cf6",
  light = "#f5f3ff",
  badge = "#6d28d9",
}) => {
  const [expanded, setExpanded] = React.useState({});
  const [page, setPage] = React.useState(0);

  React.useEffect(() => {
    setPage(0);
    setExpanded({});
  }, [rows]);

  const totalPages = Math.ceil(rows.length / SEC_PAGE_SIZE);
  const pageRows = rows.slice(page * SEC_PAGE_SIZE, (page + 1) * SEC_PAGE_SIZE);

  const getTxnTypeStyle = (type) => {
    const t = String(type || "").toUpperCase();
    if (t === "DEBIT")
      return { bg: "#fef2f2", color: "#dc2626", label: "Debit" };
    if (t === "CREDIT")
      return { bg: "#f0fdf4", color: "#16a34a", label: "Credit" };
    if (t === "FEE") return { bg: "#f1f5f9", color: "#64748b", label: "Fee" };
    return { bg: "#f8fafc", color: "#475569", label: type };
  };

  const getAmtColor = (type) => {
    const t = String(type || "").toUpperCase();
    if (t === "DEBIT") return "#dc2626";
    if (t === "CREDIT") return "#16a34a";
    return "#64748b";
  };

  const buildSummary = (bankTxns) => {
    const total = bankTxns.length;
    const completed = bankTxns.filter(
      (t) => String(t.status || "").toUpperCase() === "COMPLETED",
    ).length;
    const totalDebit = bankTxns
      .filter((t) => String(t.transactionType || "").toUpperCase() === "DEBIT")
      .reduce((s, t) => s + (Number(t.transferAmount) || 0), 0);
    const dates = bankTxns.map((t) => t.txnDateTime).filter(Boolean);
    const lastUpdated = dates.length
      ? new Date(Math.max(...dates.map((d) => new Date(d).getTime())))
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          .replace(/ /g, "-")
      : "—";
    return { total, completed, totalDebit, lastUpdated };
  };

  if (rows.length === 0)
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          padding: 40,
        }}
      >
        <div style={{ fontSize: 36, opacity: 0.15 }}>📄</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#94a3b8" }}>
          No NOCA Documents
        </div>
        <div style={{ fontSize: 12, color: "#cbd5e1" }}>
          No documents match the current filters
        </div>
      </div>
    );

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          {/* ── Header ──────────────────────────────────────────────────────── */}
          <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <tr style={{ background: "#fafbfc" }}>
              <th
                style={{
                  width: 44,
                  padding: "11px 12px",
                  borderBottom: "2px solid #e2e8f0",
                }}
              />
              {[
                { key: "docId", label: "Doc ID", align: "left" },
                { key: "documentType", label: "Document Type", align: "left" },
                {
                  key: "referenceType",
                  label: "Reference Type",
                  align: "left",
                },
                { key: "status", label: "Status", align: "center" },
              ].map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: "11px 16px",
                    borderBottom: "2px solid #e2e8f0",
                    background: "#fafbfc",
                    textAlign: col.align,
                    fontWeight: 700,
                    color: "#64748b",
                    fontSize: 11,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          {/* ── Body ────────────────────────────────────────────────────────── */}
          <tbody>
            {pageRows.map((row, i) => {
              const rowIdx = i + page * SEC_PAGE_SIZE;
              const isOpen = !!expanded[rowIdx];
              const bankTxns = row.bankTransactions ?? [];
              const summary = buildSummary(bankTxns);

              return (
                <React.Fragment key={rowIdx}>
                  {/* ── Parent row ──────────────────────────────────────────── */}
                  <tr
                    onClick={() =>
                      setExpanded((prev) =>
                        prev[rowIdx] ? {} : { [rowIdx]: true },
                      )
                    }
                    style={{
                      background: isOpen
                        ? "#f5f3ff"
                        : i % 2 === 0
                          ? "#fff"
                          : "#fafbfc",
                      borderBottom: isOpen ? "none" : "1px solid #f1f5f9",
                      cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isOpen) e.currentTarget.style.background = "#faf9ff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isOpen
                        ? "#f5f3ff"
                        : i % 2 === 0
                          ? "#fff"
                          : "#fafbfc";
                    }}
                  >
                    {/* Chevron */}
                    <td
                      style={{
                        width: 44,
                        padding: "12px 12px",
                        verticalAlign: "middle",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 26,
                          height: 26,
                          borderRadius: 6,
                          background: isOpen ? "#8b5cf6" : "#f1f5f9",
                          color: isOpen ? "#fff" : "#94a3b8",
                          transition: "background 0.2s, color 0.2s",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            transform: isOpen
                              ? "rotate(-180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.22s ease",
                          }}
                        >
                          <KeyboardArrowDownIcon style={{ fontSize: 16 }} />
                        </div>
                      </div>
                    </td>

                    {/* Doc ID */}
                    <td
                      style={{ padding: "12px 16px", verticalAlign: "middle" }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#8b5cf6",
                          letterSpacing: "0.02em",
                        }}
                      >
                        #{row.docId ?? "—"}
                      </span>
                    </td>

                    {/* Document Type */}
                    <td
                      style={{ padding: "12px 16px", verticalAlign: "middle" }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#1e293b",
                        }}
                      >
                        {row.documentType ?? "—"}
                      </span>
                    </td>

                    {/* Reference Type */}
                    <td
                      style={{ padding: "12px 16px", verticalAlign: "middle" }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "3px 10px",
                          borderRadius: 6,
                          background: "#f1f5f9",
                          color: "#475569",
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                        }}
                      >
                        {row.referenceType ?? "—"}
                      </span>
                    </td>

                    {/* Status */}
                    <td
                      style={{
                        padding: "12px 16px",
                        verticalAlign: "middle",
                        textAlign: "center",
                      }}
                    >
                      <StatusChip value={row.status} />
                    </td>
                  </tr>

                  {/* ── Expanded bank transactions ───────────────────────────── */}
                  {isOpen && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          padding: 0,
                          background: "#faf9ff",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <div
                          style={{
                            borderLeft: "3px solid #8b5cf6",
                            margin: "0 0 0 20px",
                            padding: "14px 16px 18px",
                          }}
                        >
                          {/* Section title */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              marginBottom: 14,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#6d28d9",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                              }}
                            >
                              Bank Transactions
                            </span>
                            <span
                              style={{
                                background: "#ede9fe",
                                color: "#6d28d9",
                                borderRadius: 20,
                                padding: "1px 9px",
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {bankTxns.length}
                            </span>
                          </div>

                          {bankTxns.length === 0 ? (
                            <div
                              style={{
                                color: "#94a3b8",
                                fontSize: 12,
                                padding: "10px 0",
                              }}
                            >
                              No bank transactions for this document.
                            </div>
                          ) : (
                            <>
                              {/* Transactions table */}
                              <div
                                style={{
                                  borderRadius: 10,
                                  border: "1px solid #e2e8f0",
                                  overflow: "hidden",
                                  background: "#fff",
                                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                                }}
                              >
                                <table
                                  style={{
                                    width: "100%",
                                    tableLayout: "fixed",
                                    borderCollapse: "collapse",
                                    fontSize: 12,
                                  }}
                                >
                                  <colgroup>
                                    <col style={{ width: "17%" }} />{" "}
                                    {/* Transaction ID */}
                                    <col style={{ width: "7%" }} />{" "}
                                    {/* Type          */}
                                    <col style={{ width: "9%" }} />{" "}
                                    {/* Amount        */}
                                    <col style={{ width: "7%" }} />{" "}
                                    {/* From          */}
                                    <col style={{ width: "7%" }} />{" "}
                                    {/* To            */}
                                    <col style={{ width: "10%" }} />{" "}
                                    {/* Receiver Type */}
                                    <col style={{ width: "13%" }} />{" "}
                                    {/* Status        */}
                                    <col style={{ width: "18%" }} />{" "}
                                    {/* For           */}
                                    <col style={{ width: "12%" }} />{" "}
                                    {/* Date & Time   */}
                                  </colgroup>
                                  <thead>
                                    <tr style={{ background: "#fafbfc" }}>
                                      {BANK_TXN_COLS.map((col) => (
                                        <th
                                          key={col.key}
                                          title={col.label}
                                          style={{
                                            padding: "8px 8px",
                                            borderBottom: "1px solid #e2e8f0",
                                            textAlign: "center",
                                            fontWeight: 700,
                                            color: "#94a3b8",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            fontSize: 10,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.07em",
                                          }}
                                        >
                                          {col.label}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {bankTxns.map((txn, j) => {
                                      const typeStyle = getTxnTypeStyle(
                                        txn.transactionType,
                                      );
                                      const amtColor = getAmtColor(
                                        txn.transactionType,
                                      );
                                      return (
                                        <tr
                                          key={txn.id ?? j}
                                          style={{
                                            background:
                                              j % 2 === 0 ? "#fff" : "#fafbfc",
                                            borderBottom:
                                              j < bankTxns.length - 1
                                                ? "1px solid #f1f5f9"
                                                : "none",
                                          }}
                                        >
                                          {BANK_TXN_COLS.map((col) => {
                                            const val = txn[col.key];
                                            return (
                                              <td
                                                key={col.key}
                                                title={
                                                  col.key !== "txnDateTime" &&
                                                  val != null
                                                    ? String(val)
                                                    : undefined
                                                }
                                                style={{
                                                  padding: "8px 8px",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace:
                                                    col.key === "txnFor"
                                                      ? "normal"
                                                      : "nowrap",
                                                  textAlign: "center",
                                                  verticalAlign: "middle",
                                                }}
                                              >
                                                {col.key === "status" ? (
                                                  <StatusChip value={val} />
                                                ) : col.key ===
                                                  "transactionType" ? (
                                                  <span
                                                    style={{
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      padding: "2px 8px",
                                                      borderRadius: 10,
                                                      background: typeStyle.bg,
                                                      color: typeStyle.color,
                                                      fontSize: 10,
                                                      fontWeight: 700,
                                                      letterSpacing: "0.04em",
                                                      textTransform:
                                                        "uppercase",
                                                    }}
                                                  >
                                                    {typeStyle.label}
                                                  </span>
                                                ) : col.key ===
                                                  "transferAmount" ? (
                                                  <span
                                                    style={{
                                                      fontWeight: 700,
                                                      fontVariantNumeric:
                                                        "tabular-nums",
                                                      color: amtColor,
                                                      fontSize: 13,
                                                    }}
                                                  >
                                                    {val != null
                                                      ? `₹${fmtAmount(val)}`
                                                      : "—"}
                                                  </span>
                                                ) : col.key === "txnDateTime" &&
                                                  val ? (
                                                  (() => {
                                                    const dt = new Date(val);
                                                    const date = dt
                                                      .toLocaleDateString(
                                                        "en-GB",
                                                        {
                                                          day: "2-digit",
                                                          month: "short",
                                                          year: "numeric",
                                                        },
                                                      )
                                                      .replace(/ /g, "-");
                                                    const time =
                                                      dt.toLocaleTimeString(
                                                        "en-GB",
                                                        {
                                                          hour: "2-digit",
                                                          minute: "2-digit",
                                                          hour12: false,
                                                        },
                                                      );
                                                    return (
                                                      <div>
                                                        <div
                                                          style={{
                                                            fontWeight: 500,
                                                            color: "#1e293b",
                                                            fontSize: 12,
                                                          }}
                                                        >
                                                          {date}
                                                        </div>
                                                        <div
                                                          style={{
                                                            fontSize: 10,
                                                            color: "#94a3b8",
                                                            marginTop: 1,
                                                          }}
                                                        >
                                                          {time}
                                                        </div>
                                                      </div>
                                                    );
                                                  })()
                                                ) : col.key ===
                                                  "transactionId" ? (
                                                  <span
                                                    style={{
                                                      fontSize: 11,
                                                      color: "#475569",
                                                    }}
                                                  >
                                                    {val ?? "—"}
                                                  </span>
                                                ) : (
                                                  <span
                                                    style={{
                                                      fontSize: 12,
                                                      color:
                                                        val != null
                                                          ? "#334155"
                                                          : "#94a3b8",
                                                    }}
                                                  >
                                                    {val ?? "—"}
                                                  </span>
                                                )}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 16px",
            borderTop: "1px solid #f1f5f9",
            background: "#fafbfc",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            Showing {page * SEC_PAGE_SIZE + 1}–
            {Math.min((page + 1) * SEC_PAGE_SIZE, rows.length)} of {rows.length}
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            {[...Array(Math.min(totalPages, 7))].map((_, idx) => {
              const start = Math.max(0, Math.min(page - 3, totalPages - 7));
              const p = start + idx;
              if (p >= totalPages) return null;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  style={{
                    width: 26,
                    height: 26,
                    border: "1px solid",
                    borderRadius: 6,
                    fontSize: 11,
                    cursor: "pointer",
                    fontWeight: p === page ? 700 : 400,
                    borderColor: p === page ? accent : "#e2e8f0",
                    background: p === page ? light : "#fff",
                    color: p === page ? badge : "#475569",
                  }}
                >
                  {p + 1}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const PaymentTable = ({
  rows,
  cols,
  chipKeys = [],
  accent = "#f97316",
  light = "#fff7ed",
  badge = "#c2410c",
  onRowClick,
  onViewCharges,
}) => {
  const [page, setPage] = React.useState(0);
  React.useEffect(() => {
    setPage(0);
  }, [rows]);

  const totalPages = Math.ceil(rows.length / SEC_PAGE_SIZE);
  const pageRows = rows.slice(page * SEC_PAGE_SIZE, (page + 1) * SEC_PAGE_SIZE);

  if (rows.length === 0)
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#94a3b8",
          gap: 8,
          fontSize: 13,
        }}
      >
        <span style={{ fontSize: 32, opacity: 0.3 }}>—</span>
        No records found
      </div>
    );

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <div
        style={{ flex: 1, overflowX: "auto", overflowY: "auto", minHeight: 0 }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
        >
          <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <tr>
              {cols.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: "9px 14px",
                    background: "#f8fafc",
                    borderBottom: `2px solid ${accent}`,
                    textAlign: col.key === "amount" ? "right" : "left",
                    fontWeight: 600,
                    color: "#64748b",
                    whiteSpace: "nowrap",
                    fontSize: 11,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {col.label}
                </th>
              ))}
              {onRowClick && (
                <th
                  style={{
                    padding: "9px 14px",
                    background: "#f8fafc",
                    borderBottom: `2px solid ${accent}`,
                    fontWeight: 600,
                    color: "#64748b",
                    whiteSpace: "nowrap",
                    fontSize: 11,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    width: 60,
                    textAlign: "center",
                  }}
                >
                  View
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr
                key={i}
                style={{
                  background: i % 2 === 0 ? "#fff" : "#fafbfc",
                  borderBottom: "1px solid #f1f5f9",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = `${accent}10`)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background =
                    i % 2 === 0 ? "#fff" : "#fafbfc")
                }
              >
                {cols.map((col) => {
                  const val = row[col.key];
                  let cell;
                  if (chipKeys.includes(col.key)) {
                    cell = <StatusChip value={val} />;
                  } else if (col.key === "amount") {
                    const n = Number(val);
                    cell = (
                      <span
                        style={{
                          display: "block",
                          textAlign: "right",
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                          color: n === 0 ? "#94a3b8" : "#1e293b",
                        }}
                      >
                        {n === 0 ? "₹0" : `₹${fmtAmount(val)}`}
                      </span>
                    );
                  } else if (col.key === "dateTime" && val) {
                    const parts = String(val).split(" ");
                    cell = (
                      <div>
                        <div
                          style={{
                            fontWeight: 500,
                            color: "#1e293b",
                            fontSize: 12,
                          }}
                        >
                          {parts[0]}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#94a3b8",
                            marginTop: 1,
                          }}
                        >
                          {parts[1]}
                        </div>
                      </div>
                    );
                  } else if (col.key === "viewCharges") {
                    const charges = row.walletAccount?.charges ?? [];
                    cell =
                      charges.length > 0 ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewCharges && onViewCharges(charges);
                          }}
                          style={{
                            border: `1px solid ${accent}50`,
                            background: light,
                            color: badge,
                            borderRadius: 6,
                            padding: "3px 10px",
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "inherit",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = `${accent}20`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = light;
                          }}
                        >
                          View ({charges.length})
                        </button>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>—</span>
                      );
                  } else if (col.key === "consumerId") {
                    const listing = getAuditConsumerListing(row);
                    cell = (
                      <span
                        title={String(listing ?? "")}
                        style={{ color: listing ? "#334155" : "#94a3b8" }}
                      >
                        {listing || "—"}
                      </span>
                    );
                  } else if (col.key === "againstGeneratorId") {
                    const listing = getAuditGeneratorListing(row);
                    cell = (
                      <span
                        title={String(listing ?? "")}
                        style={{ color: listing ? "#334155" : "#94a3b8" }}
                      >
                        {listing || "—"}
                      </span>
                    );
                  } else if (col.key === "paymentType") {
                    cell = <PaymentTypeChip value={val} />;
                  } else {
                    cell = (
                      <span
                        title={String(val ?? "")}
                        style={{ color: val ? "#334155" : "#94a3b8" }}
                      >
                        {val ?? "—"}
                      </span>
                    );
                  }
                  return (
                    <td
                      key={col.key}
                      style={{
                        padding: "7px 14px",
                        maxWidth: 240,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace:
                          col.key === "dateTime" ? "normal" : "nowrap",
                        color: "#334155",
                        fontSize: 12,
                        verticalAlign: "middle",
                      }}
                    >
                      {cell}
                    </td>
                  );
                })}
                {onRowClick && (
                  <td
                    style={{
                      padding: "7px 14px",
                      width: 60,
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    <button
                      onClick={() => onRowClick(row)}
                      title="View details"
                      style={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        color: "#3b82f6",
                        transition: "all 0.15s",
                        padding: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#eff6ff";
                        e.currentTarget.style.color = "#1d4ed8";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "none";
                        e.currentTarget.style.color = "#3b82f6";
                      }}
                    >
                      <VisibilityIcon style={{ fontSize: 16 }} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 16px",
            borderTop: "1px solid #f1f5f9",
            background: "#fafbfc",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            Showing {page * SEC_PAGE_SIZE + 1}–
            {Math.min((page + 1) * SEC_PAGE_SIZE, rows.length)} of {rows.length}
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            {[...Array(Math.min(totalPages, 7))].map((_, idx) => {
              const start = Math.max(0, Math.min(page - 3, totalPages - 7));
              const p = start + idx;
              if (p >= totalPages) return null;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  style={{
                    width: 26,
                    height: 26,
                    border: "1px solid",
                    borderRadius: 6,
                    fontSize: 11,
                    cursor: "pointer",
                    fontWeight: p === page ? 700 : 400,
                    borderColor: p === page ? accent : "#e2e8f0",
                    background: p === page ? light : "#fff",
                    color: p === page ? badge : "#475569",
                  }}
                >
                  {p + 1}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Searchable Select ───────────────────────────────────────────────────────
const highlightMatch = (text, q) => {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        style={{
          background: "#fff3cd",
          color: "#1e293b",
          borderRadius: 2,
          padding: "0 1px",
        }}
      >
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
};

const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  error = false,
  footerLabel = "companies",
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const ref = React.useRef(null);

  React.useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const selected = options.find((o) => String(o.value) === String(value));

  const triggerStyle = {
    width: "100%",
    height: 38,
    padding: "0 10px 0 12px",
    border: `1px solid ${error ? "#ef4444" : open ? "var(--primary-color-text)" : "#e4e7ed"}`,
    borderRadius: 8,
    background: disabled ? "#f5f5f5" : "#fafafa",
    fontSize: 13,
    color: selected ? "#333" : "#b0b3bb",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    boxShadow: open ? "0 0 0 3px rgba(241,147,92,0.12)" : "none",
    transition: "all 0.15s",
    fontFamily: "inherit",
    outline: "none",
  };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((o) => !o);
          setQuery("");
        }}
        style={triggerStyle}
      >
        <span
          style={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        {open ? (
          <KeyboardArrowUpIcon
            style={{ fontSize: 18, color: "#6f6d7c", flexShrink: 0 }}
          />
        ) : (
          <KeyboardArrowDownIcon
            style={{ fontSize: 18, color: "#6f6d7c", flexShrink: 0 }}
          />
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #e4e7ed",
            borderRadius: 10,
            boxShadow: "0 8px 28px rgba(0,0,0,0.13)",
            zIndex: 200,
            overflow: "hidden",
            maxHeight: 280,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Search input */}
          <div
            style={{
              padding: "8px 8px 6px",
              borderBottom: "1px solid #f1f5f9",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                background: "#f8fafc",
                border: "1px solid #e4e7ed",
                borderRadius: 7,
                padding: "5px 10px",
              }}
            >
              <SearchIcon
                style={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }}
              />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 12,
                  color: "#333",
                  width: "100%",
                  fontFamily: "inherit",
                }}
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#94a3b8",
                    padding: 0,
                    lineHeight: 1,
                    display: "flex",
                  }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "14px 12px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: 12,
                }}
              >
                No results found
              </div>
            ) : (
              filtered.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                      setQuery("");
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "8px 12px",
                      border: "none",
                      background: isSelected ? "#fff7ed" : "transparent",
                      color: isSelected ? "#c2410c" : "#334155",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: isSelected ? 600 : 400,
                      fontFamily: "inherit",
                      borderBottom: "1px solid #f8fafc",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.background = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span>{highlightMatch(opt.label, query)}</span>
                    {isSelected && (
                      <span style={{ fontSize: 14, color: "var(--primary-color-text)" }}>✓</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer count */}
          <div
            style={{
              padding: "5px 12px",
              borderTop: "1px solid #f1f5f9",
              fontSize: 10,
              color: "#94a3b8",
              flexShrink: 0,
              background: "#fafafa",
            }}
          >
            {filtered.length} of {options.length} {footerLabel}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AuditModal = ({ open, onClose }) => {
  const companies =
    useSelector((s) => s?.gnaAdminSlice?.fetchCompany?.data) || [];
  const role = useSelector((s) => s?.persistedReducer?.userType);

  const [filters, setFilters] = useState(INIT_FILTERS);
  const [entities, setEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "dateTime", dir: "desc" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [paymentSections, setPaymentSections] = useState(null);
  const [activePaymentTab, setActivePaymentTab] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [chargesModal, setChargesModal] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [isConsumerListing, setIsConsumerListing] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isContractData, setIsContractData] = useState(false);
  const [listingBank, setListingBank] = useState(null);
  const [contractBank, setContractBank] = useState(null);
  const [viewMode, setViewMode] = useState("listings");
  const [listingDetailData, setListingDetailData] = useState(null);
  const [apiError, setApiError] = useState("");

  // Derived flags
  const isDateRange = DATE_RANGE_TYPES.has(filters.auditType);
  const isListingNo = filters.auditType === LISTING_NO_TYPE;
  const isContractId = filters.auditType === CONTRACT_ID_TYPE;

  // ── Fetch entities when company changes ─────────────────────────────────────
  useEffect(() => {
    if (!filters.companyId) {
      setEntities([]);
      setFilters((f) => ({ ...f, entityId: "", entityType: "" }));
      return;
    }
    const load = async () => {
      setEntitiesLoading(true);
      try {
        const [cr, gr] = await Promise.allSettled([
          getConsumersByCompId(filters.companyId),
          getGeneratorsByCompId(filters.companyId),
        ]);
        const consumers = (
          cr.status === "fulfilled" ? cr.value?.data?.data || [] : []
        ).map((e) => ({
          id: e.consumerId,
          name: e.consumerName || e.entityName || `Consumer ${e.consumerId}`,
          type: "CONSUMER",
        }));
        const generators = (
          gr.status === "fulfilled" ? gr.value?.data?.data || [] : []
        ).map((e) => ({
          id: e.generatorId,
          name: e.generatorName || e.entityName || `Generator ${e.generatorId}`,
          type: "GENERATOR",
        }));
        setEntities([...consumers, ...generators]);
      } catch {
        ErrorMessage("Failed to load entities");
      } finally {
        setEntitiesLoading(false);
      }
    };
    load();
  }, [filters.companyId]);

  // ── Audit type change — clear unrelated fields ───────────────────────────────
  const handleTypeChange = (newType) => {
    const goingToDateRange = DATE_RANGE_TYPES.has(newType);
    setFilters((f) => ({
      ...f,
      auditType: newType,
      startDate: goingToDateRange ? f.startDate : "",
      endDate: goingToDateRange ? f.endDate : "",
      listingNumber: newType === LISTING_NO_TYPE ? f.listingNumber : "",
      contractId: newType === CONTRACT_ID_TYPE ? f.contractId : "",
    }));
    setErrors({});
    setResults([]);
    setSearched(false);
    setFiltersOpen(true); // always expand so the user can see the new input fields
  };

  // ── Core fetch ───────────────────────────────────────────────────────────────
  const fetchAudit = async (currentPage = 0, overrideSize = null) => {
    const errs = {};

    if (isDateRange) {
      if (!filters.companyId) errs.companyId = "Please select a company";
      if (!filters.startDate) errs.startDate = "Start date is required";
      if (!filters.endDate) errs.endDate = "End date is required";
      if (
        filters.startDate &&
        filters.endDate &&
        filters.startDate > filters.endDate
      )
        errs.endDate = "End date must be after start date";
    } else if (isListingNo) {
      if (!filters.listingNumber.trim()) {
        errs.listingNumber = "Listing number is required";
      } else if (!parseListingNumber(filters.listingNumber)) {
        errs.listingNumber =
          "Invalid format — e.g. C6184_MAY0326_MAY0426 or G8144_MAY0326_MAY0426";
      }
    } else if (isContractId) {
      if (!filters.contractId.trim())
        errs.contractId = "Contract ID is required";
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setApiError("");
    setLoading(true);
    setSearched(true);
    setFiltersOpen(false);

    let fn, params;
    if (filters.auditType === "PAYMENT_ACTIVITY") {
      fn = getAuditPaymentLogs;
      params = {
        fromDate: `${filters.startDate} 00:00:00`,
        toDate: `${filters.endDate} 23:59:59`,
        ...(filters.entityId
          ? {
              entityId: Number(filters.entityId),
              entityType: filters.entityType,
            }
          : {}),
      };
    } else if (filters.auditType === "ALL_LISTING") {
      fn = getAuditListingLogs;
      params = {
        fromDate: `${filters.startDate} 00:00:00`,
        toDate: `${filters.endDate} 23:59:59`,
        ...(filters.entityId
          ? {
              entityId: Number(filters.entityId),
              entityType: filters.entityType,
            }
          : {}),
      };
    } else if (isListingNo) {
      fn = getAuditListingDetail;
      const parsed = parseListingNumber(filters.listingNumber);
      params = { capacityId: parsed.capacityId, entityType: parsed.entityType };
    } else {
      fn = getAuditContract;
      params = { contractUniqueId: filters.contractId };
    }

    try {
      const res = await fn(params);

      if (!res || res instanceof Error || !res.data) {
        const msg =
          (res instanceof Error ? res.message : null) ||
          "API call failed — check network or auth token";
        setApiError(msg);
        setResults([]);
        setTotalCount(0);
        return;
      }

      if (filters.auditType === "PAYMENT_ACTIVITY") {
        const _code = res?.data?.code;
        const payload =
          _code != null && Number(_code) === 200
            ? (res.data.data ?? res.data)
            : res?.data;
        const walletActivities = payload?.walletActivities ?? [];
        const transactions = payload?.transactions ?? [];
        const nocaDocuments = payload?.nocaDocuments ?? [];
        if (
          walletActivities.length ||
          transactions.length ||
          nocaDocuments.length
        ) {
          setPaymentSections({ walletActivities, transactions, nocaDocuments });
          setTotalCount(
            walletActivities.length +
              transactions.length +
              nocaDocuments.length,
          );
          setActivePaymentTab(0);
          setPage(currentPage);
        } else {
          setPaymentSections(null);
          setTotalCount(0);
        }
      } else {
        // Unwrap response — supports both wrapped { code, data } and direct payloads
        const rawCode = res?.data?.code;
        const payload =
          rawCode != null && Number(rawCode) === 200
            ? (res.data.data ?? res.data)
            : res.data;

        if (!payload) {
          const msg =
            res?.data?.message ||
            res?.data?.error ||
            `Server returned code ${rawCode} — no data`;
          setApiError(msg);
          setResults([]);
          setTotalCount(0);
          return;
        }

        // ── Type-aware parsing ────────────────────────────────────────────────
        if (isListingNo) {
          // Response: { capacityId, entityType, consumerListing, revisionHistory, contracts }
          setListingDetailData(payload);
          setResults([]);
          setTotalCount(0);
          setListingBank(null);
          setContractBank(null);
        } else if (filters.auditType === "ALL_LISTING") {
          // Response: { consumerListings: [...], contracts: [...], generatorListings: [...] }
          const listings =
            payload?.consumerListings || payload?.generatorListings || [];
          const contracts =
            payload?.contracts || payload?.bilateralContracts || [];

          setListingBank(listings);
          setContractBank(contracts);
          setViewMode("listings");
          setResults(listings);
          setTotalCount(listings.length);
          setIsConsumerListing(true);
          setIsContractData(false);
        } else if (isContractId) {
          // Response: { contract: {...}, revisionHistory: [...] }
          setListingDetailData(payload);
          setResults([]);
          setTotalCount(0);
          setIsContractData(false);
          setIsConsumerListing(false);
          setListingBank(null);
          setContractBank(null);
        } else {
          // Fallback — paginated content
          setResults(
            payload?.content ?? (Array.isArray(payload) ? payload : []),
          );
          setTotalCount(
            payload?.totalElements ??
              (Array.isArray(payload) ? payload.length : 0),
          );
          setIsConsumerListing(false);
          setIsContractData(false);
          setListingBank(null);
          setContractBank(null);
        }

        setPage(currentPage);
      }
    } catch (e) {
      const msg = e?.message || "Unexpected error — check browser console";
      setApiError(msg);
      setResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(INIT_FILTERS);
    setEntities([]);
    setResults([]);
    setPaymentSections(null);
    setIsConsumerListing(false);
    setIsContractData(false);
    setListingBank(null);
    setContractBank(null);
    setListingDetailData(null);
    setViewMode("listings");
    setApiError("");
    setActivePaymentTab(0);
    setSearch("");
    setSort({ key: "dateTime", dir: "desc" });
    setPage(0);
    setPageSize(10);
    setErrors({});
    setSearched(false);
    setTotalCount(0);
  };

  const handleEntityChange = (val) => {
    const found = entities.find((e) => `${e.type}-${e.id}` === val);
    setFilters((f) => ({
      ...f,
      entityId: found ? String(found.id) : "",
      entityType: found?.type || "",
    }));
  };

  const handleSort = (key) => {
    setSort((s) => ({
      key,
      dir: s.key === key && s.dir === "asc" ? "desc" : "asc",
    }));
  };

  // ── Derived display rows (listing / contract types) ─────────────────────────
  const displayRows = useMemo(() => {
    let rows = [...results];
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        Object.values(r).some((v) =>
          String(v ?? "")
            .toLowerCase()
            .includes(q),
        ),
      );
    }
    rows.sort((a, b) => {
      const av = String(a[sort.key] ?? "");
      const bv = String(b[sort.key] ?? "");
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return rows;
  }, [results, search, sort]);

  // ── Filtered payment sections ────────────────────────────────────────────────
  const filteredWallet = useMemo(() => {
    const rows = paymentSections?.walletActivities ?? [];
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [paymentSections, search]);

  const filteredTransactions = useMemo(() => {
    const rows = paymentSections?.transactions ?? [];
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [paymentSections, search]);

  const filteredNoca = useMemo(() => {
    const rows = paymentSections?.nocaDocuments ?? [];
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [paymentSections, search]);

  const isPayment = filters.auditType === "PAYMENT_ACTIVITY";
  const cols = isPayment
    ? PAYMENT_COLS
    : isContractData
      ? CONTRACT_DETAIL_COLS
      : isConsumerListing
        ? CONSUMER_LISTING_COLS
        : LISTING_COLS;
  const totalPages = Math.ceil(totalCount / pageSize);
  // ALL_LISTING data is fetched all at once — paginate client-side
  const isClientSidePagination = isConsumerListing || isContractData;
  const activeTotalCount = isClientSidePagination ? displayRows.length : totalCount;
  const activeTotalPages = isClientSidePagination
    ? Math.ceil(displayRows.length / pageSize)
    : totalPages;

  const exportSuffix = isDateRange
    ? `${filters.startDate}_to_${filters.endDate}`
    : isListingNo
      ? filters.listingNumber || "listing"
      : filters.contractId || "contract";
  const exportName = `audit_${filters.auditType.toLowerCase()}_${exportSuffix}`;
  const exportTitle = `Audit Log — ${AUDIT_TYPES.find((t) => t.value === filters.auditType)?.label ?? filters.auditType}`;

  if (!open || role !== "SUPER_ADMIN") return null;

  return (
    <>
      <div
        className={Styles.overlay}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className={Styles.container}>
          {/* ── Header ───────────────────────────────────────────────────────────── */}
          <div className={Styles.header}>
            <div className={Styles.headerLeft}>
              <AssessmentIcon className={Styles.headerIcon} />
              <div>
                <div className={Styles.headerTitle}>Audit Log</div>
                <div className={Styles.headerSub}>
                  Generate and export platform audit reports — Super Admin only
                </div>
              </div>
            </div>
            <button className={Styles.closeBtn} onClick={onClose} title="Close">
              <CloseIcon />
            </button>
          </div>

          {/* ── Body ─────────────────────────────────────────────────────────────── */}
          <div className={Styles.body}>
            {/* Filters Card */}
            <div className={Styles.filtersCard}>
              {/* ── Header ─────────────────────────────────────────────────────── */}
              <div
                className={Styles.filtersTitle}
                onClick={() => setFiltersOpen((o) => !o)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <FilterAltIcon style={{ fontSize: 14 }} />
                  Filters
                </span>
                <button
                  className={Styles.collapseBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltersOpen((o) => !o);
                  }}
                >
                  {filtersOpen ? (
                    <KeyboardArrowUpIcon style={{ fontSize: 13 }} />
                  ) : (
                    <KeyboardArrowDownIcon style={{ fontSize: 13 }} />
                  )}
                  {filtersOpen ? "Collapse" : "Expand"}
                </button>
              </div>

              {filtersOpen && (
                <>
                  {/* ── Single-row field grid ─────────────────────────────────── */}
                  <div className={Styles.filtersGrid}>
                    {/* 1 — Audit Type */}
                    <div className={Styles.filterField}>
                      <label className={Styles.label}>
                        Audit Type <span className={Styles.req}>*</span>
                      </label>
                      <select
                        className={Styles.select}
                        value={filters.auditType}
                        onChange={(e) => handleTypeChange(e.target.value)}
                      >
                        {AUDIT_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* 2 — Company */}
                    <div className={Styles.filterField}>
                      <label className={Styles.label}>
                        Company
                        {isDateRange && <span className={Styles.req}> *</span>}
                      </label>
                      <SearchableSelect
                        options={companies.map((c) => ({
                          value: c.companyId,
                          label: c.companyName,
                        }))}
                        value={filters.companyId}
                        onChange={(val) => {
                          setFilters((f) => ({
                            ...f,
                            companyId: val,
                            entityId: "",
                            entityType: "",
                          }));
                          setErrors((er) => ({ ...er, companyId: "" }));
                        }}
                        placeholder="Select company…"
                        error={!!errors.companyId}
                      />
                      {errors.companyId && (
                        <span className={Styles.errorMsg}>
                          {errors.companyId}
                        </span>
                      )}
                    </div>

                    {/* 3 — Entity */}
                    <div className={Styles.filterField}>
                      <label className={Styles.label}>Entity</label>
                      <SearchableSelect
                        options={entities.map((e) => ({
                          value: `${e.type}-${e.id}`,
                          label: `[${e.type}] ${e.name}`,
                        }))}
                        value={
                          filters.entityId && filters.entityType
                            ? `${filters.entityType}-${filters.entityId}`
                            : ""
                        }
                        onChange={(val) => handleEntityChange(val)}
                        placeholder={
                          entitiesLoading ? "Loading…" : "Select entity"
                        }
                        disabled={!filters.companyId || entitiesLoading}
                        footerLabel="entities"
                      />
                    </div>

                    {/* 4a — Date range */}
                    {isDateRange && (
                      <>
                        <div className={Styles.filterField}>
                          <label className={Styles.label}>
                            Start Date <span className={Styles.req}>*</span>
                          </label>
                          <input
                            type="date"
                            className={`${Styles.dateInput} ${errors.startDate ? Styles.inputError : ""}`}
                            value={filters.startDate}
                            max={filters.endDate || undefined}
                            onChange={(e) => {
                              setFilters((f) => ({
                                ...f,
                                startDate: e.target.value,
                              }));
                              setErrors((er) => ({ ...er, startDate: "" }));
                            }}
                          />
                          {errors.startDate && (
                            <span className={Styles.errorMsg}>
                              {errors.startDate}
                            </span>
                          )}
                        </div>
                        <div className={Styles.filterField}>
                          <label className={Styles.label}>
                            End Date <span className={Styles.req}>*</span>
                          </label>
                          <input
                            type="date"
                            className={`${Styles.dateInput} ${errors.endDate ? Styles.inputError : ""}`}
                            value={filters.endDate}
                            min={filters.startDate || undefined}
                            onChange={(e) => {
                              setFilters((f) => ({
                                ...f,
                                endDate: e.target.value,
                              }));
                              setErrors((er) => ({ ...er, endDate: "" }));
                            }}
                          />
                          {errors.endDate && (
                            <span className={Styles.errorMsg}>
                              {errors.endDate}
                            </span>
                          )}
                        </div>
                      </>
                    )}

                    {/* 4b — Listing Number */}
                    {isListingNo && (
                      <div className={Styles.filterField}>
                        <label className={Styles.label}>
                          Listing Number <span className={Styles.req}>*</span>
                        </label>
                        <input
                          type="text"
                          className={`${Styles.textInput} ${errors.listingNumber ? Styles.inputError : ""}`}
                          placeholder="e.g. C6184_MAY0326_MAY0426"
                          value={filters.listingNumber}
                          onChange={(e) => {
                            setFilters((f) => ({
                              ...f,
                              listingNumber: e.target.value,
                            }));
                            setErrors((er) => ({ ...er, listingNumber: "" }));
                          }}
                        />
                        {errors.listingNumber && (
                          <span className={Styles.errorMsg}>
                            {errors.listingNumber}
                          </span>
                        )}
                      </div>
                    )}

                    {/* 4c — Contract ID */}
                    {isContractId && (
                      <div className={Styles.filterField}>
                        <label className={Styles.label}>
                          Contract ID <span className={Styles.req}>*</span>
                        </label>
                        <input
                          type="text"
                          className={`${Styles.textInput} ${errors.contractId ? Styles.inputError : ""}`}
                          placeholder="Enter contract ID…"
                          value={filters.contractId}
                          onChange={(e) => {
                            setFilters((f) => ({
                              ...f,
                              contractId: e.target.value,
                            }));
                            setErrors((er) => ({ ...er, contractId: "" }));
                          }}
                        />
                        {errors.contractId && (
                          <span className={Styles.errorMsg}>
                            {errors.contractId}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Footer ─────────────────────────────────────────────────── */}
                  <div className={Styles.filterActions}>
                    <button className={Styles.resetBtn} onClick={handleReset}>
                      <RestartAltIcon style={{ fontSize: 14 }} />
                      Reset Filters
                    </button>
                    <button
                      className={Styles.generateBtn}
                      onClick={() => fetchAudit(0)}
                      disabled={loading}
                    >
                      {loading ? (
                        <span className={Styles.spinnerInline} />
                      ) : (
                        <AssessmentIcon style={{ fontSize: 14 }} />
                      )}
                      Generate Audit
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Results Card — shown after first search */}
            {(searched || loading) && (
              <div className={Styles.resultsCard}>
                {/* Results header */}
                <div className={Styles.resultsHeader}>
                  <div className={Styles.resultsInfo}>
                    {!loading && isPayment && paymentSections && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            background: "#ffedd5",
                            color: "#c2410c",
                            borderRadius: 20,
                            padding: "3px 11px",
                            fontWeight: 700,
                            border: "1px solid #f9733620",
                          }}
                        >
                          {paymentSections.walletActivities.length} Wallet
                          Transaction
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            background: "#dbeafe",
                            color: "#1d4ed8",
                            borderRadius: 20,
                            padding: "3px 11px",
                            fontWeight: 700,
                            border: "1px solid #3b82f620",
                          }}
                        >
                          {paymentSections.transactions.length} Contracts
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            background: "#ede9fe",
                            color: "#6d28d9",
                            borderRadius: 20,
                            padding: "3px 11px",
                            fontWeight: 700,
                            border: "1px solid #8b5cf620",
                          }}
                        >
                          {paymentSections.nocaDocuments.length} NOCA Documents
                        </span>
                      </div>
                    )}
                    {!loading && !isPayment && (
                      <span className={Styles.resultsCount}>
                        {totalCount.toLocaleString()} record
                        {totalCount !== 1 ? "s" : ""} found
                      </span>
                    )}
                    {!loading && !isPayment && listingBank && contractBank && (
                      <div
                        style={{
                          display: "flex",
                          gap: 4,
                          background: "#f1f5f9",
                          borderRadius: 8,
                          padding: 3,
                        }}
                      >
                        {[
                          {
                            mode: "listings",
                            label: `Listings (${listingBank.length})`,
                            color: "#8b5cf6",
                          },
                          {
                            mode: "contracts",
                            label: `Contracts (${contractBank.length})`,
                            color: "#f97316",
                          },
                        ].map(({ mode, label, color }) => {
                          const active = viewMode === mode;
                          return (
                            <button
                              key={mode}
                              onClick={() => {
                                setViewMode(mode);
                                setPage(0);
                                if (mode === "listings") {
                                  setResults(listingBank);
                                  setTotalCount(listingBank.length);
                                  setIsConsumerListing(true);
                                  setIsContractData(false);
                                } else {
                                  setResults(contractBank);
                                  setTotalCount(contractBank.length);
                                  setIsContractData(true);
                                  setIsConsumerListing(false);
                                }
                              }}
                              style={{
                                padding: "5px 14px",
                                borderRadius: 6,
                                border: "none",
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: active ? 700 : 500,
                                background: active ? "#fff" : "transparent",
                                color: active ? color : "#64748b",
                                boxShadow: active
                                  ? "0 1px 4px rgba(0,0,0,0.1)"
                                  : "none",
                                transition: "all 0.15s",
                                fontFamily: "inherit",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {loading && (
                      <span className={Styles.loadingLabel}>
                        Fetching audit logs...
                      </span>
                    )}
                  </div>
                  <div className={Styles.resultsActions}>
                    <div className={Styles.searchBox}>
                      <SearchIcon className={Styles.searchIcon} />
                      <input
                        type="text"
                        placeholder="Search results..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        className={Styles.searchInput}
                      />
                    </div>

                    {!loading && isPayment && paymentSections && (
                      <div className={Styles.exportGroup}>
                        <button
                          className={Styles.exportBtn}
                          title="Export all sections as Excel"
                          onClick={() =>
                            downloadAllPaymentExcel(
                              filteredWallet,
                              filteredTransactions,
                              filteredNoca,
                              exportName,
                            )
                          }
                        >
                          <FileDownloadIcon style={{ fontSize: 13 }} /> Excel
                          (All Tabs)
                        </button>
                      </div>
                    )}

                    {!loading && listingDetailData && (
                      <div className={Styles.exportGroup}>
                        <button
                          className={Styles.exportBtn}
                          title="Export all sections as Excel"
                          onClick={() =>
                            listingDetailData?.contract
                              ? downloadContractDetailExcel(
                                  listingDetailData,
                                  exportName,
                                )
                              : downloadListingDetailExcel(
                                  listingDetailData,
                                  exportName,
                                )
                          }
                        >
                          <FileDownloadIcon style={{ fontSize: 13 }} /> Excel
                        </button>
                      </div>
                    )}

                    {!loading && !isPayment && results.length > 0 && (
                      <div className={Styles.exportGroup}>
                        <button
                          className={Styles.exportBtn}
                          onClick={() =>
                            downloadCSV(displayRows, cols, exportName)
                          }
                          title="Export as CSV"
                        >
                          <FileDownloadIcon style={{ fontSize: 13 }} /> CSV
                        </button>
                        <button
                          className={Styles.exportBtn}
                          onClick={() =>
                            downloadExcel(displayRows, cols, exportName)
                          }
                          title="Export as Excel"
                        >
                          <FileDownloadIcon style={{ fontSize: 13 }} /> Excel
                        </button>
                        {listingBank && contractBank && (
                          <button
                            className={Styles.exportBtn}
                            title="Export Listings + Contracts as multi-sheet Excel"
                            onClick={() =>
                              downloadAllListingExcel(
                                listingBank,
                                contractBank,
                                exportName,
                              )
                            }
                          >
                            <FileDownloadIcon style={{ fontSize: 13 }} /> Excel
                            (All Sheets)
                          </button>
                        )}
                        <button
                          className={Styles.exportBtn}
                          onClick={() =>
                            downloadPDF(
                              displayRows,
                              cols,
                              exportName,
                              exportTitle,
                            )
                          }
                          title="Export as PDF"
                        >
                          <FileDownloadIcon style={{ fontSize: 13 }} /> PDF
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Table area */}
                <div className={Styles.tableWrap}>
                  {apiError ? (
                    <div className={Styles.emptyState}>
                      <span
                        style={{ fontSize: 28, color: "#ef4444", opacity: 0.7 }}
                      >
                        ✕
                      </span>
                      <span style={{ color: "#991b1b", fontSize: 14 }}>
                        API Error
                      </span>
                      <p
                        style={{
                          color: "#ef4444",
                          fontSize: 12,
                          maxWidth: 480,
                          textAlign: "center",
                          wordBreak: "break-word",
                        }}
                      >
                        {apiError}
                      </p>
                    </div>
                  ) : loading ? (
                    <div className={Styles.loadingState}>
                      <span className={Styles.spinner} />
                      <span>Fetching audit logs...</span>
                    </div>
                  ) : listingDetailData ? (
                    <AuditListingDetailView data={listingDetailData} />
                  ) : isPayment ? (
                    paymentSections ? (
                      (() => {
                        const tabRows = [
                          filteredWallet,
                          filteredTransactions,
                          filteredNoca,
                        ];
                        const tab = TAB_CONFIG[activePaymentTab];
                        return (
                          <>
                            {/* ── Tab bar ─────────────────────────────────────── */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                borderBottom: "2px solid #f1f5f9",
                                background: "#fff",
                                flexShrink: 0,
                              }}
                            >
                              {TAB_CONFIG.map((t, i) => {
                                const totalCount = [
                                  paymentSections.walletActivities.length,
                                  paymentSections.transactions.length,
                                  paymentSections.nocaDocuments.length,
                                ][i];
                                const active = i === activePaymentTab;
                                return (
                                  <button
                                    key={i}
                                    onClick={() => setActivePaymentTab(i)}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 7,
                                      padding: "11px 18px",
                                      border: "none",
                                      borderBottom: `2px solid ${active ? t.accent : "transparent"}`,
                                      marginBottom: "-2px",
                                      background: active
                                        ? t.light
                                        : "transparent",
                                      color: active ? t.badge : "#64748b",
                                      fontWeight: active ? 700 : 500,
                                      cursor: "pointer",
                                      fontSize: 13,
                                      transition: "all 0.15s",
                                      fontFamily: "inherit",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {t.label}
                                    <span
                                      style={{
                                        background: active
                                          ? t.badgeBg
                                          : "#f1f5f9",
                                        color: active ? t.badge : "#94a3b8",
                                        borderRadius: 20,
                                        padding: "1px 8px",
                                        fontSize: 11,
                                        fontWeight: 700,
                                        transition: "all 0.15s",
                                      }}
                                    >
                                      {totalCount}
                                    </span>
                                  </button>
                                );
                              })}
                              <div style={{ flex: 1 }} />
                              <div
                                style={{
                                  padding: "0 12px",
                                  display: "flex",
                                  gap: 6,
                                  alignItems: "center",
                                }}
                              >
                                {/* CSV — always available */}
                                <button
                                  onClick={() =>
                                    downloadCSV(
                                      tabRows[activePaymentTab],
                                      tab.cols,
                                      `${exportName}_${tab.exportKey}`,
                                    )
                                  }
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "4px 12px",
                                    border: `1px solid ${tab.accent}50`,
                                    borderRadius: 6,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    background: tab.light,
                                    color: tab.badge,
                                    fontFamily: "inherit",
                                  }}
                                >
                                  <FileDownloadIcon style={{ fontSize: 12 }} />{" "}
                                  CSV
                                </button>
                                {/* Excel — contracts tab gets Contracts + Charges + Wallet Activities sheets */}
                                <button
                                  onClick={() =>
                                    activePaymentTab === 1
                                      ? downloadContractsTabExcel(
                                          tabRows[1],
                                          tabRows[0],
                                          `${exportName}_contracts`,
                                        )
                                      : activePaymentTab === 2
                                        ? downloadNocaExcel(
                                            tabRows[2],
                                            `${exportName}_${tab.exportKey}`,
                                          )
                                        : downloadWalletExcel(
                                            tabRows[0],
                                            `${exportName}_${tab.exportKey}`,
                                          )
                                  }
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "4px 12px",
                                    border: `1px solid ${tab.accent}50`,
                                    borderRadius: 6,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    background: tab.light,
                                    color: tab.badge,
                                    fontFamily: "inherit",
                                  }}
                                >
                                  <FileDownloadIcon style={{ fontSize: 12 }} />
                                  {activePaymentTab === 1
                                    ? "Excel (All Sheets)"
                                    : "Excel"}
                                </button>
                              </div>
                            </div>

                            {/* ── Active tab table ─────────────────────────────── */}
                            {activePaymentTab === 2 ? (
                              <NocaAccordionTable
                                rows={tabRows[2]}
                                accent={TAB_CONFIG[2].accent}
                                light={TAB_CONFIG[2].light}
                                badge={TAB_CONFIG[2].badge}
                              />
                            ) : (
                              <PaymentTable
                                rows={tabRows[activePaymentTab]}
                                cols={tab.cols}
                                chipKeys={tab.chipKeys}
                                accent={tab.accent}
                                light={tab.light}
                                badge={tab.badge}
                                onRowClick={
                                  activePaymentTab === 1
                                    ? setSelectedTransaction
                                    : undefined
                                }
                                onViewCharges={
                                  activePaymentTab === 1
                                    ? setChargesModal
                                    : undefined
                                }
                              />
                            )}
                          </>
                        );
                      })()
                    ) : (
                      <div className={Styles.emptyState}>
                        <AssessmentIcon
                          style={{ fontSize: 42, opacity: 0.2 }}
                        />
                        <span>No records found</span>
                        <p>Try adjusting your filters or search criteria</p>
                      </div>
                    )
                  ) : displayRows.length === 0 ? (
                    <div className={Styles.emptyState}>
                      <AssessmentIcon style={{ fontSize: 42, opacity: 0.2 }} />
                      <span>No records found</span>
                      <p>Try adjusting your filters or search criteria</p>
                    </div>
                  ) : (
                    <>
                      <div className={Styles.table}>
                        <div className={Styles.tableHead}>
                          {cols.map((col) => (
                            <div
                              key={col.key}
                              className={`${Styles.th} ${Styles[`col_${col.key}`] || Styles.colDefault}`}
                              onClick={() => handleSort(col.key)}
                            >
                              {col.label}
                              <span className={Styles.sortIcon}>
                                {sort.key === col.key ? (
                                  sort.dir === "asc" ? (
                                    <KeyboardArrowUpIcon
                                      style={{ fontSize: 13 }}
                                    />
                                  ) : (
                                    <KeyboardArrowDownIcon
                                      style={{ fontSize: 13 }}
                                    />
                                  )
                                ) : (
                                  <UnfoldMoreIcon
                                    style={{ fontSize: 13, opacity: 0.35 }}
                                  />
                                )}
                              </span>
                            </div>
                          ))}
                          {(isConsumerListing || isContractData) && (
                            <div
                              className={Styles.th}
                              style={{
                                width: 60,
                                justifyContent: "center",
                                flex: "0 0 60px",
                              }}
                            >
                              View
                            </div>
                          )}
                        </div>
                        <div className={Styles.tableBody}>
                          {(isClientSidePagination
                            ? displayRows.slice(page * pageSize, (page + 1) * pageSize)
                            : displayRows
                          ).map((row, i) => (
                            <div key={i} className={Styles.tableRow}>
                              {cols.map((col) => (
                                <div
                                  key={col.key}
                                  className={`${Styles.td} ${Styles[`col_${col.key}`] || Styles.colDefault}`}
                                >
                                  {col.key === "status" ||
                                  col.key === "settlementStatus" ? (
                                    <StatusChip value={row[col.key]} />
                                  ) : col.key === "createdAt" ? (
                                    <span>
                                      {row[col.key]?.split("T")?.[0] ?? "—"}
                                    </span>
                                  ) : (
                                    <span title={String(row[col.key] ?? "")}>
                                      {row[col.key] ?? "—"}
                                    </span>
                                  )}
                                </div>
                              ))}
                              {(isConsumerListing || isContractData) && (
                                <div
                                  className={Styles.td}
                                  style={{
                                    flex: "0 0 60px",
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      isContractData
                                        ? setSelectedContract(row)
                                        : setSelectedListing(row)
                                    }
                                    title="View details"
                                    style={{
                                      border: "none",
                                      background: "none",
                                      cursor: "pointer",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 28,
                                      height: 28,
                                      borderRadius: 6,
                                      color: "#3b82f6",
                                      padding: 0,
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background =
                                        "#eff6ff";
                                      e.currentTarget.style.color = "#1d4ed8";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = "none";
                                      e.currentTarget.style.color = "#3b82f6";
                                    }}
                                  >
                                    <VisibilityIcon style={{ fontSize: 16 }} />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {activeTotalPages > 1 && (
                        <div className={Styles.pagination}>
                          <span className={Styles.pageInfo}>
                            Page {page + 1} of {activeTotalPages} ·{" "}
                            {activeTotalCount.toLocaleString()} records
                          </span>
                          <div className={Styles.pageControls}>
                            <select
                              className={Styles.pageSizeSelect}
                              value={pageSize}
                              onChange={(e) => {
                                const newSize = Number(e.target.value);
                                setPageSize(newSize);
                                if (isClientSidePagination) {
                                  setPage(0);
                                } else {
                                  fetchAudit(0, newSize);
                                }
                              }}
                            >
                              {PAGE_SIZE_OPTIONS.map((n) => (
                                <option key={n} value={n}>
                                  {n} / page
                                </option>
                              ))}
                            </select>
                            <div className={Styles.pageBtns}>
                              <button
                                className={Styles.pageBtn}
                                disabled={page === 0}
                                onClick={() =>
                                  isClientSidePagination
                                    ? setPage(page - 1)
                                    : fetchAudit(page - 1)
                                }
                              >
                                ‹
                              </button>
                              {Array.from(
                                { length: Math.min(5, activeTotalPages) },
                                (_, i) => {
                                  const p =
                                    Math.max(
                                      0,
                                      Math.min(page - 2, activeTotalPages - 5),
                                    ) + i;
                                  return (
                                    <button
                                      key={p}
                                      className={`${Styles.pageBtn} ${p === page ? Styles.pageBtnActive : ""}`}
                                      onClick={() =>
                                        isClientSidePagination
                                          ? setPage(p)
                                          : fetchAudit(p)
                                      }
                                    >
                                      {p + 1}
                                    </button>
                                  );
                                },
                              )}
                              <button
                                className={Styles.pageBtn}
                                disabled={page >= activeTotalPages - 1}
                                onClick={() =>
                                  isClientSidePagination
                                    ? setPage(page + 1)
                                    : fetchAudit(page + 1)
                                }
                              >
                                ›
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedTransaction && (
        <TransactionDetailModal
          txn={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
      {chargesModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.5)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => setChargesModal(null)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              width: "min(700px, 96vw)",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 20px",
                background: "linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%)",
                borderBottom: "1px solid #fed7aa",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}
                >
                  Charges
                </span>
                <span
                  style={{
                    background: "#f59e0b",
                    color: "#fff",
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1.6,
                  }}
                >
                  {chargesModal.length}
                </span>
              </div>
              <button
                onClick={() => setChargesModal(null)}
                style={{
                  border: "none",
                  background: "#f1f5f9",
                  borderRadius: 8,
                  width: 30,
                  height: 30,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#64748b",
                  flexShrink: 0,
                }}
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            </div>

            {/* Table */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr>
                    {[
                      { label: "Name", align: "left" },
                      { label: "Charge Type", align: "left" },
                      { label: "Value", align: "right" },
                      { label: "Frequency", align: "left" },
                      { label: "Type", align: "left" },
                    ].map((h) => (
                      <th
                        key={h.label}
                        style={{
                          padding: "10px 14px",
                          textAlign: h.align,
                          background: "#f8fafc",
                          borderBottom: "2px solid #f59e0b",
                          color: "#64748b",
                          fontWeight: 600,
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {chargesModal.map((c, i) => {
                    const chargeTypeColors = {
                      Platform: { bg: "#eff6ff", color: "#1d4ed8" },
                      Trader: { bg: "#f0fdf4", color: "#166534" },
                      "State/STU": { bg: "#f1f5f9", color: "#475569" },
                    };
                    const ctCfg = chargeTypeColors[c.chargeType] || {
                      bg: "#f1f5f9",
                      color: "#475569",
                    };
                    const freqColors = {
                      RECURRING: { bg: "#f0fdf4", color: "#166534" },
                      ONE_TIME: { bg: "#fff7ed", color: "#c2410c" },
                    };
                    const freqCfg = freqColors[c.frequency] || {
                      bg: "#f1f5f9",
                      color: "#64748b",
                    };
                    const typeColors = {
                      FIXED: { bg: "#f1f5f9", color: "#475569" },
                      PER_MW: { bg: "#f5f3ff", color: "#6d28d9" },
                      PERCENTAGE: { bg: "#eff6ff", color: "#1d4ed8" },
                    };
                    const typeCfg = typeColors[c.type] || {
                      bg: "#f1f5f9",
                      color: "#64748b",
                    };
                    const chipStyle = (cfg) => ({
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "2px 9px",
                      borderRadius: 10,
                      background: cfg.bg,
                      color: cfg.color,
                      fontSize: 11,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    });
                    return (
                      <tr
                        key={c.id ?? i}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          background: i % 2 === 0 ? "#fff" : "#fafbfc",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#fffbeb")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            i % 2 === 0 ? "#fff" : "#fafbfc")
                        }
                      >
                        <td
                          style={{
                            padding: "10px 14px",
                            color: "#1e293b",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.name ?? "—"}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={chipStyle(ctCfg)}>
                            {c.chargeType ?? "—"}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "10px 14px",
                            textAlign: "right",
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                            color:
                              Number(c.value) === 0 ? "#94a3b8" : "#1e293b",
                          }}
                        >
                          {c.value != null ? c.value : "—"}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={chipStyle(freqCfg)}>
                            {c.frequency ? c.frequency.replace(/_/g, " ") : "—"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          {c.type ? (
                            <span style={chipStyle(typeCfg)}>
                              {c.type.replace(/_/g, " ")}
                            </span>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {selectedListing && (
        <ListingDetailModal
          listing={selectedListing}
          contracts={
            contractBank?.filter(
              (c) =>
                c.capacityConsumerName === selectedListing?.name ||
                c.capacityGeneratorName === selectedListing?.name
            ) || []
          }
          onClose={() => setSelectedListing(null)}
        />
      )}
      {selectedContract && (
        <ContractDetailModal
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
        />
      )}
    </>
  );
};

export default AuditModal;
